/**
 * Data Management table registry — the single source of truth for every table
 * that lives under Data Management.
 *
 * One row per table. The registry drives:
 *   - the Data Management hub page (cards grouped by `group`, filtered by page access)
 *   - GlobalSearch entries (so a table is findable from the top bar)
 *   - per-table routes + PermissionGate (engine tables only — generated in DashboardLayout)
 *
 * Two kinds of rows:
 *   - ENGINE rows carry `fields` and are rendered by the shared <TablemaintPage>
 *     (the Dispatchers-style drawer UX) talking to the generic /tablemaint backend.
 *   - CUSTOM rows (`custom: true`) are the existing hand-built standalone pages; the
 *     registry only links to their existing `route`. They get converted onto the
 *     engine later, one at a time.
 *
 * NOTE: this registry is for the UI (labels, grouping, routing, field rendering).
 * The backend keeps its own authoritative whitelist (columns, editable, dup-guard
 * keys, schema, validation, dev-write gate) — never trust the client for those.
 */

import { EQUIPMENT_SIZES, EQUIPMENT_TYPES, CHASSIS_TYPES } from '../constants/orderFieldOptions';
import { RepairItemTypes } from '../types/repairOrders';

export type FieldType = 'text' | 'number' | 'select' | 'yn' | 'toggle' | 'date' | 'textarea' | 'multiselect' | 'lookup';

export interface TablemaintField {
  /** Column name on the row / backend field key. */
  key: string;
  /** Human label shown as the column header and the drawer field label. */
  label: string;
  /** Optional grid column-header override (defaults to `label`). Use when the
   *  compact table header should differ from the more descriptive drawer field
   *  label — e.g. 'Sync Interval' in the table, 'Sync Interval (seconds)' in the
   *  drawer. */
  columnLabel?: string;
  /** Render type. Defaults to 'text'. 'yn' renders a checkbox but stores 'Y'/'N'. */
  type?: FieldType;
  /** Required on save — drives the asterisk, red outline, and the error banner. */
  required?: boolean;
  /** Conditionally required: this field is required only when another field currently
   *  holds a given value (e.g. Driver Access Name is required when Driver Access = 'Y').
   *  Drives the same asterisk / red outline / error banner as `required`, but only while
   *  the condition holds. The backend validator must enforce the same rule. */
  requiredWhen?: { field: string; equals: string };
  maxLength?: number;
  /** Options for `select`. Plain strings or {value,label}. */
  options?: (string | { value: string; label: string })[];
  /** Editable in the drawer. Default true. false → shown in the table only. */
  editable?: boolean;
  /** Editable on create but locked on update (maps to backend `update_locked`). */
  createOnly?: boolean;
  /** Show as a table column. Default true. */
  inTable?: boolean;
  /** TanTable column id, when it must differ from `key` (the row's data key).
   *  Set only by the dynamic-column picker: TanTable identifies a dynamic column by
   *  getApiFieldName(dbName), and seven legacy names are renamed by that map (see
   *  useGridColumnPicker). Leave unset everywhere else — changing a column's id
   *  invalidates the layout users have saved against it. */
  colId?: string;
  /** Column width hint. */
  width?: number;
  /** Table-cell alignment. */
  align?: 'left' | 'center' | 'right';
  /** Display the value Title-Cased (legacy stores ALLCAPS). Display only. */
  titleCase?: boolean;
  /** Render the cell as a color-coded key-account badge (shared color scheme). */
  keyAccount?: boolean;
  /** Render a non-empty cell value in red/danger styling (e.g. a *DELETED* /
   *  *EXPIRED* status flag). Display only; blank values render nothing. */
  redFlag?: boolean;
  /** Render the cell as a color-coded status pill, keyed by the cell's exact value
   *  (e.g. { MAPPED: 'green', 'NOT MAPPED': 'red' }). Values not in the map render
   *  as a gray pill; blank values render nothing. Display only. */
  badgeColors?: Record<string, 'green' | 'amber' | 'red' | 'blue' | 'gray'>;
  /** Render the value as the standard CMS pro-number link: a blue link that opens
   *  the pro in the ProDetailsView drawer (+ an external legacy-CMS icon), same as
   *  the Dispatch / LC dashboards. The cell also gets the shared viewed-PRO pill
   *  (green once clicked, green ring on the last one opened). Display only. */
  proLink?: boolean;
  /** Render a newline-joined (CHAR(10)) value as a vertical stack: the first line
   *  normal, subsequent lines muted (e.g. a tariff's primary Bill To on top with its
   *  additional bill-tos below). Opts the column out of single-line truncation.
   *  Combine with `titleCase` to Title-Case each line. Display only. */
  stackedLines?: boolean;
  /** The cell holds a comma-separated LIST of values rather than one (e.g. every
   *  PRO an EDI invoice covers, aggregated server-side so the invoice stays ONE
   *  row). The column accessor returns the split array, so a column filter, its
   *  facets and a pasted Match List all match each value on its own; the cell
   *  still renders the joined string. Without it the grid compares a pasted PRO
   *  against the whole "5094281, 5094282" cell and drops the row — even on a
   *  server-resolved column, where the server already returned it correctly. */
  multiValue?: boolean;
  /** Append the row's `idKey` column as a muted "#id" after the cell value —
   *  shows the FK recno with a joined display name (e.g. a rule's Bill To name
   *  with its billtos.recno). Nothing is appended when the id column is blank.
   *  Display only; sorting/filtering still use the name column. */
  idKey?: string;
  /** Row column holding a comma-separated list of THIS cell's entries that are no
   *  longer current (e.g. an EDI handle's LCs who have left). Those names render
   *  struck through in danger colour after the current ones, with an "N / total"
   *  count, and an explicit "no active LC" marker when none are left. Use when
   *  filtering the departed entries out of the query would make the ROW disappear
   *  rather than merely shorten the list. Display only. */
  inactiveKey?: string;
  /** Row column holding the referenced entity's own soft-delete flag ('Y'/'N').
   *  When 'Y' the grid cell renders red with a small "(deleted)" marker, and — on a
   *  select/lookup field — the drawer shows a red note beneath it ("This customer has
   *  been deleted."). Flags rules pointing at a since-deleted customer/location/bill-to. */
  deletedKey?: string;
  /** Y/N companion flag; N adds a (not approved) warning to a populated cell. */
  approvedKey?: string;
  /** Row column holding the referenced entity's NAME, for a select/lookup field whose
   *  stored value is a recno. FK option lists return only ACTIVE rows, so a reference
   *  to a since-deleted record would otherwise fall out of the list and render as a
   *  bare number. With this set the drawer keeps it visible as "Name  ·  #recno"
   *  instead. Pair with `deletedKey` to also explain why. */
  refNameKey?: string;
  placeholder?: string;
  /** Dynamic select options: a GET endpoint returning string[]. <TablemaintPage>
   *  fetches it once and uses the result as this select's options. */
  optionsEndpoint?: string;
  /** Offer distinct nonblank values already present in this table as choices. */
  optionsFromRows?: boolean;
  /** Let a select store newly typed text as well as picked values. */
  allowNewOptions?: boolean;
  /** Allow clearing a select back to blank (default true). false for must-pick
   *  selects whose option list has no blank entry (e.g. Whse Instructions). */
  clearable?: boolean;
  /** For a select / optionsEndpoint field: render each option's `description` (e.g. a
   *  customer's address + code) as a second line in the dropdown. Useful for FK pickers
   *  whose options carry a description (e.g. /cms-lookups/customers). */
  showDescriptions?: boolean;
  /** For a select / optionsEndpoint field: render the SELECTED option's info line
   *  (address, Sage tag, …) muted beneath the field, so the picked record's detail is
   *  visible without reopening the dropdown. Pairs with `showDescriptions`. */
  showSelectedInfo?: boolean;
  /** For `type: 'lookup'`: which async FK picker to render, keyed into LOOKUP_FIELDS
   *  (see lookupFields.tsx) — 'billto' | 'location' | 'port' | 'carrier'. Use this
   *  instead of `optionsEndpoint` when the target is too large to preload (carriers is
   *  ~23,700 active rows): it searches the server per keystroke and renders a result
   *  card per hit. Pair with `refNameKey` so the stored FK's name shows on open, and
   *  with `fkZeroBlank` so a cleared value writes back as '0'. */
  lookupTarget?: string;
  /** Legacy FK recno column that stores 0 for "unset" (billto, customercode,
   *  locationcode, …). The drawer shows 0 as blank and writes a cleared value back
   *  as '0' rather than '', matching what the engine's dup-guard and the V1 columns
   *  expect. */
  fkZeroBlank?: boolean;
  /** Value pre-filled on a new (create) record, overriding the per-type default. */
  defaultValue?: string | boolean;
  /** Span both columns of the drawer's 2-col input grid (full width). Handy for
   *  a lone field or a long free-text entry. */
  fullWidth?: boolean;
  /** Drawer section this field belongs to. Fields sharing a `group` render together
   *  inside one full-width <fieldset> with the group name as its legend — the
   *  semantic sections the bespoke drawers hand-built ('Identity (read-only)',
   *  'Settings', 'Customer Options', 'Notes'). A group holds whatever its fields
   *  are: read-only ones render as read-only, inputs as inputs, yn/toggle as
   *  toggles. Groups render after the ungrouped fields, in first-seen order.
   *  Ungrouped fields are unaffected, so tables that don't set this are unchanged. */
  group?: string;
  /** Validate (when non-empty) as a positive whole number. Blank is allowed unless
   *  also `required`. Drives the drawer's inline error + red outline. */
  integer?: boolean;
  /** Validate (when non-empty) as a positive number (decimals allowed). Blank is allowed
   *  unless also `required`. Pair with `type: 'number'`. */
  decimal?: boolean;
  /** Validate (when non-empty) as one or more email addresses separated by commas or
   *  semicolons (shared isValidEmailList). Blank is allowed unless also `required`. */
  email?: boolean;
  /** Render a "show on map" button beneath this text field. When set and the value is a
   *  non-empty ZIP, the button opens ZipLocationMapModal (geocodes the ZIP + drops a pin). */
  mapZip?: boolean;
}

export type TableGroup =
  | 'cms'
  | 'quote'
  | 'safety'
  | 'locations'
  | 'warehouse'
  | 'carriers'
  | 'equipment'
  | 'rates'
  | 'billing'
  | 'driver'
  | 'driverpay'
  | 'tracing'
  | 'integrations'
  | 'reports'
  | 'admin';

export interface DataTableDef {
  /** Backend table key for the engine; also the registry id. */
  key: string;
  /** Display name (hub card, table title). */
  label: string;
  /** Hub subcategory. */
  group: TableGroup;
  /** Optional subheader within the group — tables sharing a `subgroup` render
   *  under one subheading on the hub (e.g. Integrations → 'EDI' | 'Melrose' |
   *  'Upload'). Tables with no subgroup render directly under the group heading.
   *  Subgroups appear in first-seen registry order. */
  subgroup?: string;
  /** Route path — also the permission_pages key for engine tables. */
  route: string;
  /** Optional one-line hub-card subtitle. */
  description?: string;

  // ── Engine rows ──────────────────────────────────────────────────────────
  /** Field specs. Present → rendered by <TablemaintPage>. */
  fields?: TablemaintField[];
  /** Primary-key column. Default 'recno'. */
  idKey?: string;
  /** Soft-delete column. Default 'deleted' (CHAR(1) 'Y'/'N'). */
  softDelete?: string;
  /** The soft-delete column stores an ACTIVE flag, inverted from `deleted`:
   *  'Y' = active/visible, 'N' = soft-deleted (e.g. carrierprofile.active). The
   *  grid renders it as an "Active" column, reds-out inactive rows, and the
   *  show-deleted toggle reveals inactive ones. (The backend already inverts via
   *  soft_delete_active='Y' / soft_delete_deleted='N'.) */
  softDeleteInvert?: boolean;
  /** Table has no soft-delete — suppress the Deleted column, show-deleted toggle,
   *  and the drawer's Deleted checkbox (e.g. quotepdfmessage). */
  noSoftDelete?: boolean;
  /** Browse-only — no add/edit/delete (e.g. the Salesforce mirror). */
  readonly?: boolean;
  /** Suppress the grid's drag-select mass edit for this table, even on columns the
   *  backend would allow. For a table where changing one field across many records
   *  is meaningful in isolation but wrong in bulk (a rate that must be re-derived,
   *  a mapping that pairs two columns). Single-record editing is unaffected. */
  noMassEdit?: boolean;
  /** Show a second "Show Archived" toggle (like Show Deleted) that folds in an
   *  archive table on demand — the backend list_sql receives include_archived
   *  (e.g. EDI Events + edi_events_archive). */
  showArchivedToggle?: boolean;
  /** Offer "retrigger everything in view" in the table's action bar.
   *
   *  The write is the EDI history's Retrigger — set resend='Y' on EDI Invoices, clear
   *  the sent marker on EDI Events — so `source` is the SAME vocabulary the per-record
   *  action uses and the meaning stays defined in one place on the server.
   *
   *  Its scope is the table's filtered rows, which is what makes it useful: narrow the
   *  grid to the invoices you mean (the pasted-list filter takes 180 numbers at once),
   *  then act on all of them without selecting a single row. */
  bulkRetrigger?: {
    /** Server-side source key, e.g. 'edisentinvoices' | 'edievents'. Omit when the
     *  grid mixes sources and `sourceBy` names them per row. */
    source?: string;
    /** Button label. The action bar already shows an "N in view" chip beside it, so
     *  this says what will happen, not how many. */
    label: string;
    /** Shown in the confirm dialog above the count. */
    confirm: string;
    /** For a grid whose rows do NOT all belong to the same server source: read
     *  `column` off each row and map its value to the source that row must be sent
     *  under. Each group goes out as its own request; a value missing from the map is
     *  reported as skipped rather than silently dropped.
     *
     *  EDI Events is why this exists. Its grid folds cms.edi_events_archive in behind
     *  the Show Archived toggle, and retriggering an archive row is a DIFFERENT write:
     *  the live rows get their sent date cleared, while an archive row is lifted back
     *  into cms.edi_events and dropped from the archive. Sending either under the
     *  other's source would be wrong about what the user asked for.
     *
     *  Note this is about MEANING, not safety. The archive job carries recno across
     *  rather than re-issuing it, so both tables share one sequence and a recno names
     *  at most one row across the pair — a mis-routed recno would match nothing rather
     *  than hit an unrelated event. */
    sourceBy?: {
      column: string;
      /** Row value -> the server source it maps to, plus what to call those rows in
       *  the confirm dialog. Singular — rendered as `N <noun>(s)`, matching how the
       *  rest of the action bar counts things. */
      map: Record<string, { source: string; noun: string }>;
    };
  };
  /** Server-side search mode for tables too large to load fully into the browser
   *  (e.g. EDI Events, ~670k rows). The page loads only a capped page (the most
   *  recent `serverSearchLimit` rows) and pushes the search box term to the server
   *  (?q=) rather than filtering client-side. The backend list_sql must accept
   *  `search` + `limit`. */
  serverSearch?: boolean;
  /** Row cap for `serverSearch` tables (default 500). */
  serverSearchLimit?: number;
  /** Columns whose pasted-list filter ("Match List") is resolved by the SERVER rather
   *  than against the loaded rows. Only meaningful on `serverSearch` tables: the
   *  browser holds a page, not the table, so a client-side match reports "not found"
   *  for anything past the cap and the user cannot tell that apart from a real miss.
   *  Must mirror the backend's TableCfg.list_filter_columns, which is authoritative —
   *  this list only decides which columns the grid routes to the server. Columns left
   *  off (and every column on an uncapped table) still filter client-side. */
  listFilterColumns?: string[];
  /** Suppress the Add affordance — rows are created elsewhere (e.g. billtos via
   *  legacy upload). Edit/delete still available. */
  createLocked?: boolean;
  /** In the generic drawer, render the non-editable (`editable: false`) fields as a
   *  read-only "Details" block above the editable inputs — useful when the editable
   *  fields need surrounding context (e.g. EDI Monitoring Master: edit notification
   *  settings while seeing which EDI handle / table the row is for). Opt-in so other
   *  generic-drawer tables are unaffected. */
  showReadOnlyContext?: boolean;
  /** Column-scoped deep-link filters: URL query params (matching backend columns) that the
   *  page forwards to the list fetch as server-side filters (e.g. Report Monitoring History
   *  opened with ?system=&name= to show one report's runs). The backend list_sql must accept
   *  a `filters` kwarg. Distinct from the `?q=` global-search deep-link. */
  filterParams?: string[];
  /** An extra link rendered beside "View Edit History" in the generic drawer — e.g. Report
   *  Monitoring Master → "View Report History", opening a filtered grid in a new tab.
   *  `href(row)` builds the target URL from the current row. */
  extraHistoryLink?: { label: string; href: (row: Record<string, any>) => string };
  /** Row-level actions rendered beside "View Edit History" in the generic drawer, by key.
   *  Resolved in `components/tablemaint/drawerActions.tsx` (e.g. 'print-test-page' on
   *  Printers). Keys only, so this config file never imports components. */
  drawerActions?: string[];
  /** Drawer width for this table's edit drawer, e.g. '56rem'. Defaults to the
   *  shared 44rem. Widen it for a table whose groups hold a lot of fields (the
   *  bespoke drawers ranged 34–80rem). The Drawer forces full width on mobile
   *  regardless, so a fixed rem value is safe. */
  drawerWidth?: string;
  /** Cross-field rule: at least one of `fields` must be set, or the save is blocked
   *  with `message` in the drawer's error banner. Per-field `required` can't express
   *  it and `requiredWhen` is a single equality condition, so a table like Container
   *  Status Report ("enter at least one of Deliver To / Bill To / Customer / Broker /
   *  CMS Code") needs this. A value of '0' counts as blank, matching how the backend
   *  validators treat an unset FK. Set `group` to render `hint` beside that field
   *  group's legend. The backend enforces the same rule — this only saves a round trip. */
  requireOneOf?: { fields: string[]; message: string; group?: string; hint?: string };
  /** A caution shown above the grid, before anything is edited. For a field whose
   *  effect reaches outside this system — a commitment, a notification, a downstream
   *  push — where finding out afterwards is too late. Pair it with the same wording
   *  beside the field itself in the drawer, so it can't be missed from either
   *  direction. Not for general help text: a banner every user learns to skip stops
   *  working for the case that needed it. */
  notice?: string;
  latestDateFilter?: { column: string; label: string };
  enableColumnEditing?: boolean;
  enableTotalsBar?: boolean;
  /** Singular noun for the "Add X" affordance. Defaults to `label`. */
  singular?: string;
  searchPlaceholder?: string;
  /** Enable TanTable's Group By toolbar for this table. Non-empty = the default
   *  grouping columns (field keys) on load; users can regroup/ungroup via the
   *  toolbar menu (e.g. Customer Per Diem Rules groups by Bill To). */
  groupBy?: string[];
  /** Sort applied on first load (TanTable's `defaultSorting`). `id` is the field
   *  key. Saved table settings and any sort the user picks still win — this only
   *  decides what an untouched grid opens on. */
  defaultSort?: { id: string; desc?: boolean }[];
  /** Default-on row filter with its own toolbar toggle — the NARROWING twin of
   *  Show Deleted / Show Archived. The grid opens showing only rows whose `column`
   *  holds a value; the funnel toggle folds the blank ones back in. Resolved
   *  SERVER-side (backend TableCfg.list_nonblank), like the other two toggles, so
   *  the default view doesn't ship the rows it is hiding. `noun` names what the
   *  kept rows have, for the toggle's tooltip ("Show only rows with errors").
   *  `icon` picks the toolbar glyph — it names the rows, the way the trash names
   *  deleted ones; defaults to the warning triangle. */
  nonBlankFilter?: { column: string; noun: string; icon?: 'alert' | 'flag' };

  // ── Custom rows ──────────────────────────────────────────────────────────
  /** true → existing standalone page; the registry only links to `route`. */
  custom?: boolean;
}

export const GROUP_LABELS: Record<TableGroup, string> = {
  cms: 'CMS Tables',
  quote: 'Quote Tables',
  safety: 'Safety Tables',
  locations: 'Locations & Warehouse',
  warehouse: 'Warehouse',
  carriers: 'SCAC & Carriers',
  equipment: 'Equipment & Reference',
  rates: 'Rates, Fuel & Per Diem',
  billing: 'Billing & Additional Costs',
  driver: 'Driver & Driver Pay',
  driverpay: 'Driver Pay',
  tracing: 'Tracing',
  integrations: 'Integrations',
  reports: 'Reports & Monitoring',
  admin: 'Admin Tables',
};

export const GROUP_ORDER: TableGroup[] = [
  'cms', 'quote', 'safety',
  'locations', 'warehouse', 'carriers', 'equipment', 'rates', 'billing', 'driver', 'driverpay',
  'tracing', 'integrations', 'reports',
  'admin',
];

const YN_OPTS = [
  { value: 'N', label: 'No' },
  { value: 'Y', label: 'Yes' },
];

// Shared option lists ported from the legacy quote TABLE_META.
// Delivery-standards city list (no blank — city is required, defaults to HOUSTON).
const CITY_OPTS = ['HOUSTON', 'DFW', 'AUSTIN', 'BALTIMORE', 'CHARLESTON', 'CHICAGO',
  'JACKSONVILLE', 'MEMPHIS', 'MOBILE', 'NEW ORLEANS', 'NORFOLK', 'SAN ANTONIO', 'SAVANNAH'];
// Handling (transload) standards — hardcoded option lists (V1 *_data).
const HS_WHSETYPE_OPTS = ['', 'ADD/REMOVE EXCESS', 'REWORK', 'FULL STRIP/STUFF', 'OUT OF GAUGE', 'RAIL TRANSLOAD'];
const HS_MOVETYPE_OPTS = ['', 'IMPORT', 'DOMESTIC', 'EXPORT', 'REPO'];
// Container Status Report's own move-type list (V1 contstatusreport MOVETYPES) — a
// superset of HS_MOVETYPE_OPTS, so deliberately NOT shared with it.
const CSR_MOVETYPE_OPTS = ['', 'IMPORT', 'EXPORT', 'DOMESTIC', 'INBOUND', 'OUTBOUND',
  'RAIL TO PORT', 'REPO'];
const HS_PACKAGETYPE_OPTS = ['', 'AUTO', 'BALES', 'BULK', 'BUNDLES', 'DRUMS',
  'HAND STACKED 0-999', 'HAND STACKED 1,000-2,000', 'HAND STACKED 2,001-3,000',
  'HAND STACKED 3,001-4,000', 'HAND STACKED 4,001-5,000', 'HAND STACKED 5,001-6,000',
  'MACHINERY', 'OTHER', 'PAPER ROLLS', 'PIPE (BUNDLED)', 'PIPE (LOOSE)', 'STEEL BARS',
  'STEEL COILS', 'SUPERSACKS (NON-PALLETIZED)', 'UNITIZED < 8,000 LBS',
  'UNITIZED 8,000 - 12,000 LBS', 'UNITIZED > 12,000 LBS'];

/** Shown at the top of the EDI Locations grid AND under the Auto Accept toggle in
 *  its drawer. Enabling Auto Accept is a commercial commitment rather than a display
 *  preference, so the same wording appears at both points a user can reach it —
 *  one string so the two can't drift apart. */
export const AUTO_ACCEPT_CAUTION =
  'If you enable Auto Accept, the system will automatically accept orders with this delivery location on your '
  + 'behalf, meaning you are committing to those orders based on the availability and '
  + "rates provided. You'll want to make sure you're comfortable with that before enabling it.";

export const DATA_TABLES: DataTableDef[] = [
  // ───────────────────────────── CMS ─────────────────────────────
  // New engine-driven tables (cms schema). First real exercise of the engine.
  {
    key: 'billtos',
    label: 'Bill Tos',
    singular: 'Bill To',
    group: 'cms',
    route: '/data-management/bill-tos',
    searchPlaceholder: 'Search bill-tos…',
    createLocked: true, // added via legacy upload only; edit-only here
    // Table-view columns (the edit experience is the custom BilltoEditDrawer).
    fields: [
      { key: 'cname', label: 'Bill To', width: 150, titleCase: true },
      { key: 'mas90code', label: 'Sage Tag', width: 80 },
      { key: 'cadd1', label: 'Address 1', width: 150, titleCase: true },
      { key: 'ccity', label: 'City', width: 100, titleCase: true },
      { key: 'cstate', label: 'State', width: 60 },
      { key: 'czip', label: 'Zip', width: 60 },
      { key: 'ccountry', label: 'Country', width: 80, titleCase: true },
      { key: 'salesrep', label: 'Sales Rep', width: 90 },
      { key: 'creditterms', label: 'Credit Terms', width: 100, titleCase: true },
      { key: 'credithold', label: 'Credit Hold', type: 'yn', width: 90, align: 'center' },
      { key: 'arbalance', label: 'A/R Balance', type: 'number', width: 100, align: 'right' },
      { key: 'recno', label: 'Record', type: 'number', width: 80, align: 'right' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    singular: 'Customer',
    group: 'cms',
    route: '/data-management/customers',
    searchPlaceholder: 'Search customers…',
    // Table-view columns (the edit/create experience is CustomersEditDrawer).
    // `mastername`, `market` and the after-hours fields are engine computed columns.
    fields: [
      { key: 'custname', label: 'Customer Name', width: 180, titleCase: true },
      { key: 'custadd', label: 'Address', width: 130, titleCase: true },
      { key: 'custcity', label: 'City', width: 90, titleCase: true },
      { key: 'custstate', label: 'ST', width: 50 },
      { key: 'custzip', label: 'Zip', width: 60 },
      { key: 'custtype', label: 'Type', width: 80, titleCase: true },
      { key: 'mastername', label: 'Master', width: 140, titleCase: true, deletedKey: 'masterdeleted' },
      { key: 'keyaccount', label: 'Key', width: 120, align: 'center', keyAccount: true },
      { key: 'market', label: 'Market', width: 120 },
      { key: 'custregion', label: 'Region', width: 90, titleCase: true },
      { key: 'allowbrokerage', label: 'Allow Bkg', type: 'yn', width: 80, align: 'center' },
      { key: 'salesrep', label: 'Sales Rep', width: 100, align: 'center' },
      { key: 'salesforceid', label: 'SF ID', width: 90 },
      { key: 'afterhourscontact', label: 'After Hours Contact', width: 140, titleCase: true },
      { key: 'afterhourscontactnotes', label: 'After Hours Contact Notes', width: 260, inTable: false, editable: false },
      { key: 'recno', label: 'Rec', type: 'number', width: 70, align: 'right' },
    ],
  },
  // Broker/Forwarder — CMS entity edited via the bespoke BrokerForwarderEditDrawer
  // (registered in customDrawers.tsx); these fields are the grid columns and the
  // engine appends the Deleted column. Contacts (contactinfo, tablename=
  // 'brokerforwarder') and the ≥1-contact-on-create rule are handled in the drawer.
  {
    key: 'brokerforwarder',
    label: 'Broker/Forwarder',
    singular: 'Broker/Forwarder',
    group: 'cms',
    route: '/data-management/broker-forwarder',
    searchPlaceholder: 'Search brokers/forwarders…',
    fields: [
      { key: 'name', label: 'Name', width: 220 },
      { key: 'address', label: 'Address', width: 200 },
      { key: 'address2', label: 'Address 2', width: 100 },
      { key: 'city', label: 'City', width: 70 },
      { key: 'state', label: 'State', width: 40 },
      { key: 'zip', label: 'Zip', width: 50 },
      { key: 'phone', label: 'Phone', width: 80 },
      { key: 'fax', label: 'Fax', width: 80 },
      { key: 'recno', label: 'Record', type: 'number', width: 45, align: 'right', editable: false },
    ],
  },
  // Carrier Profiles — highway.carrierprofile, edited via the bespoke
  // CarrierProfileEditDrawer (RMIS DOT/MC lookup + carriercontact subtable). The
  // soft-delete column is the inverted `active` flag (Y=active/visible) →
  // softDeleteInvert renders it as an "Active" column and reds-out inactive rows;
  // the show-deleted (trash) toggle reveals inactive carriers. These fields are
  // the grid columns; create/edit + RMIS + contacts all live in the drawer.
  {
    key: 'carrierprofile',
    label: 'Carrier Profiles',
    singular: 'Carrier Profile',
    group: 'cms',
    route: '/data-management/carrier-profiles',
    searchPlaceholder: 'Search carriers…',
    softDelete: 'active',
    softDeleteInvert: true,
    fields: [
      { key: 'rmis', label: 'RMIS', type: 'yn', width: 50, align: 'center' },
      { key: 'truckername', label: 'Carrier', width: 180 },
      { key: 'truckerscac', label: 'SCAC', width: 55 },
      { key: 'mcnum', label: 'MC #', width: 70 },
      { key: 'dotnum', label: 'DOT #', width: 70 },
      { key: 'truckeraddress', label: 'Address', width: 150 },
      { key: 'truckercity', label: 'City', width: 80 },
      { key: 'truckerstate', label: 'State', width: 45 },
      { key: 'truckerzip', label: 'Zip', width: 55 },
      { key: 'confirmed', label: 'Confirmed', type: 'yn', width: 70, align: 'center' },
      { key: 'lastconfirmdatetime', label: 'Last Confirmed', type: 'date', width: 90, align: 'center' },
      // Tracking (migration 416) is edited in the drawer and offered by the grid's
      // field picker, but is not a default column — hence inTable: false. The spec
      // still earns its keep there: the picker reuses it for an added column, so
      // Tracking arrives as a curated 'Tracking' BoolPill instead of the raw 'Y'/'N'
      // text a char(1) would otherwise get.
      { key: 'tracking', label: 'Tracking', type: 'yn', width: 70, align: 'center', inTable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 55, align: 'right', editable: false },
    ],
  },
  // CCC Standards (cms.ccc_standards) — warehouse commodity-handling standards.
  // Generic field-spec drawer; inverted soft-delete on `active` (Y=visible) →
  // softDeleteInvert renders the "Active" column + Active toggle in the drawer.
  // Activity is STRIP/STUFF (default STRIP, no blank); Equip Type is a fixed list
  // (blank allowed); Commodity/Activity Code/Hours required; Hours is a decimal.
  {
    key: 'ccc-standards',
    label: 'CCC Standards',
    singular: 'CCC Standard',
    group: 'warehouse',
    route: '/data-management/ccc-standards',
    searchPlaceholder: 'Search CCC standards…',
    softDelete: 'active',
    softDeleteInvert: true,
    fields: [
      { key: 'commodity', label: 'Commodity', required: true, maxLength: 25, width: 150 },
      { key: 'description', label: 'Description', maxLength: 25, width: 150 },
      { key: 'activity', label: 'Activity', type: 'select', options: ['STRIP', 'STUFF'], defaultValue: 'STRIP', clearable: false, required: true, width: 80 },
      {
        key: 'equiptype', label: 'Equip Type', type: 'select', width: 80,
        options: ['', '20', '40', '40F', '40R', '40OT', '20F', '20F/20OT', '40F/40OT', '20/40', '>49 PLTS', '39-50 PLTS', '29-40 PLTS', '<30 PLTS', 'REEFER'],
      },
      { key: 'activitycode', label: 'Activity Code', type: 'number', required: true, maxLength: 10, width: 90, align: 'right' },
      { key: 'activitydescription', label: 'Act. Description', maxLength: 50, fullWidth: true, width: 150 },
      { key: 'hours', label: 'Hours', type: 'number', required: true, width: 70, align: 'right' },
      { key: 'recno', label: 'Recno', type: 'number', width: 50, align: 'right', editable: false },
    ],
  },
  // Custom LC Groups (cms.customcsrgroup) — generic field-spec drawer, standard
  // `deleted` soft-delete. Membership is a ~-joined string of LC/CSR usernames in
  // `csrlist`; the `csrs` multiselect maps the array <-> that column (engine
  // delimited multiselect, like Dispatcher Groups). Options = the shared CSR
  // roster. Group Name + at least one member required (both full-width in the
  // drawer). Surfaced as "LC"; engine key + DB table stay `customcsrgroup`.
  {
    key: 'customcsrgroup',
    label: 'Custom LC Groups',
    singular: 'Custom LC Group',
    group: 'cms',
    route: '/data-management/custom-lc-groups',
    searchPlaceholder: 'Search custom LC groups…',
    fields: [
      { key: 'groupname', label: 'Group Name', required: true, maxLength: 30, width: 150, fullWidth: true },
      { key: 'csrs', label: 'LC List', type: 'multiselect', required: true, optionsEndpoint: '/cms-lookups/csrs', width: 300, placeholder: 'Select LCs…', fullWidth: true },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Custom Chassis Charges (cms.customchassischarges + _scac subtable) — bespoke
  // CustomChassisChargesEditDrawer (FK typeaheads + Region + inline SCAC-line
  // editor served by /customchassischarges). The grid is DENORMALIZED by SCAC line
  // (backend list_sql); recno is the parent recno so a row-click opens the parent.
  // These fields are the (read-only) grid columns; standard `deleted` soft-delete.
  {
    key: 'customchassischarges',
    label: 'Custom Chassis Charges',
    singular: 'Custom Chassis Charge',
    group: 'billing',
    route: '/data-management/custom-chassis-charges',
    searchPlaceholder: 'Search custom chassis charges…',
    // A record here is one Bill To / Customer / Location / Region / SCAC combination —
    // the columns identify the rule rather than describe it, so setting one across a
    // range would re-point rules at a record nobody chose. `region` is the only one the
    // engine would otherwise expose (the rest are FK display names with no backing
    // column in the grid), and it is a coded value that belongs in the drawer's picker,
    // not a free-text cell. The bespoke drawer edits all of them one record at a time.
    noMassEdit: true,
    fields: [
      { key: 'brokername', label: 'Broker', titleCase: true, width: 150, idKey: 'brokercode', deletedKey: 'brokerdeleted', editable: false },
      { key: 'cname', label: 'Bill To', titleCase: true, width: 130, idKey: 'billto', deletedKey: 'billtodeleted', editable: false },
      { key: 'custname', label: 'Customer', titleCase: true, width: 130, idKey: 'customercode', deletedKey: 'custdeleted', editable: false },
      { key: 'lname', label: 'Location', titleCase: true, width: 120, idKey: 'locationcode', deletedKey: 'locdeleted', editable: false },
      { key: 'region', label: 'Region', width: 80, editable: false },
      { key: 'scac', label: 'SCAC', width: 60, editable: false },
      { key: 'chassispool', label: 'Chassis Pool', width: 90, editable: false },
      { key: 'invoiced', label: 'Invoiced', type: 'yn', width: 70, align: 'center', editable: false },
      { key: 'createaddlcost', label: 'Addl Cost', type: 'yn', width: 70, align: 'center', editable: false },
      { key: 'numfreedays', label: 'Free Days', type: 'number', width: 70, align: 'right', editable: false },
      { key: 'expirationdate', label: 'Exp Date', type: 'date', width: 90, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 60, align: 'right', editable: false },
    ],
  },
  // EDI 997 Acknowledgments (cms.edi_997) — Integrations/EDI. Every detail line of
  // every inbound Kleinschmidt 997 functional acknowledgment, mapped back to the
  // record we sent (Source Table + Source Recno). REPORTING ONLY and read-only the
  // whole way down: the backend engine is readonly + list_sql (server-searched, capped
  // at 10k like EDI Events), every field here is editable:false, createLocked hides
  // Add, and the bespoke Edi997DetailDrawer is display-only with no save path.
  //
  // The grid leads with what a support question starts from (when / what / who / which
  // reference / accepted-or-not) and keeps the diagnostic tail — ISA-GS ID, Kleinschmidt's
  // control number, the source-record pointer, the ack file and line — as later columns
  // users can hide. Result spells out the raw code; Match Status matters because a 214 is
  // matched POSITIONALLY (SEQMATCH), not by key. Event names WHICH tracking event was
  // acked (214s only) — denormalised on the ack row so it outlives the event's move to
  // edi_events_archive.
  {
    key: 'edi-997',
    label: 'EDI 997 Acknowledgments',
    singular: 'EDI 997 Acknowledgment',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-997',
    description: 'Inbound functional acknowledgments for the EDI we send',
    searchPlaceholder: 'Search by Pro #, Reference #, EDI Handle, Doc Type, Event, Control #, or Match Status…',
    createLocked: true,
    serverSearch: true,
    serverSearchLimit: 10000,
    listFilterColumns: ['refnumber', 'pronumber', 'controlnumber', 'isagsid', 'edifilename'],
    fields: [
      { key: 'ackdatetime', label: 'Ack Date/Time', width: 140, editable: false },
      { key: 'doctype', label: 'Doc Type', width: 80, align: 'center', editable: false },
      // 214 only — the matched edi_events row's own event name, copied onto the ack at
      // match time so it survives that row moving to edi_events_archive. Blank on 990s.
      { key: 'event', label: 'Event', width: 140, editable: false },
      { key: 'edihandle', label: 'EDI Handle', width: 140, editable: false },
      { key: 'refnumber', label: 'Reference #', width: 110, editable: false },
      { key: 'pronumber', label: 'Pro Number', proLink: true, width: 110, editable: false },
      { key: 'acceptreject', label: 'Code', columnLabel: 'Code', width: 60, align: 'center', editable: false },
      { key: 'ackresult', label: 'Result', width: 150, editable: false },
      { key: 'matchstatus', label: 'Match Status', width: 110, editable: false },
      { key: 'processdate', label: 'Process Date', width: 110, editable: false },
      { key: 'isagsid', label: 'ISA/GS ID', width: 120, editable: false },
      { key: 'controlnumber', label: 'Control #', width: 110, editable: false },
      { key: 'tablename', label: 'Source Table', width: 150, editable: false },
      { key: 'tablerecno', label: 'Source Recno', width: 110, align: 'right', editable: false },
      { key: 'createdatetime', label: 'Processed', width: 140, editable: false },
      { key: 'edifilename', label: 'EDI File', width: 300, editable: false },
      { key: 'linenumber', label: 'Line #', type: 'number', width: 70, align: 'right', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI LCs (cms.edi_csrs) — Integrations/EDI. A many-to-many LC ↔ EDI handle,
  // presented GROUPED by handle (EDI Handle / Bill To / LC List) rather than one row
  // per pair. Bespoke EdiCsrsEditDrawer reconciles the LC set for a handle via
  // /edicsrs (insert/reactivate/soft-delete). Backend engine is read-only + list_sql
  // (grouped); createLocked stays OFF (Add maps a new handle), noSoftDelete hides the
  // Deleted column (the grouped grid shows live mappings only). idKey = edihandle
  // (the group key — the grid has no per-row recno).
  {
    key: 'edi-csrs',
    label: 'EDI LCs',
    singular: 'EDI LC Mapping',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-lcs',
    searchPlaceholder: 'Search EDI LCs…',
    idKey: 'edihandle',
    noSoftDelete: true,
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 130, editable: false },
      { key: 'billtoname', label: 'Bill To', titleCase: true, width: 320, editable: false },
      // Every live mapping, with the ones whose LC has left struck through rather
      // than filtered out — filtering them removed the whole handle from the grid,
      // and the Add drawer then refused it too because the mapping still existed.
      { key: 'lc_list', label: 'LC List', width: 320, editable: false,
        inactiveKey: 'lc_inactive' },
      { key: 'lc_inactive', label: 'Inactive LCs', inTable: false, editable: false },
      // Live cms.edi_csr_rules rows on the handle. 0 = nobody has lane-scoped it and
      // every LC works the whole handle (today's default everywhere), so a non-zero
      // value is what marks a handle whose ownership is narrowed.
      { key: 'lane_rules', label: 'Lane Rules', width: 100, editable: false },
    ],
  },
  // EDI Charge Code Mapping (cms.edi_chargecodemap) — Integrations/EDI. One row per
  // (EDI handle, invoice line-item type) -> charge code on the outbound 210, presented
  // GROUPED by handle (the raw table would be one grid row per charge code). Replaces
  // V1's hardcoded %lineitemmap. The 'DEFAULT' handle is the base map every other
  // handle layers over — it sorts first and holds ~61 rows; a partner handle stores
  // only what it does differently. Bespoke EdiChargeCodeMapEditDrawer reconciles a
  // handle's whole set via /edi-chargecodes/lines. Backend engine is read-only +
  // list_sql (grouped); createLocked stays OFF (Add maps a new handle).
  // idKey = edihandle (the group key — the grid has no per-row recno).
  //
  // Soft-delete is per MAPPING but a grid row is a HANDLE, so the row flag is the
  // group's: computed `active` = 'Y' while the handle has at least one live mapping.
  // softDeleteInvert renders it as an "Active" column and reds out a handle whose
  // mappings were all removed; the trash toggle is what brings that handle back into
  // the grid at all (its row set is gone, so it drops out of the default view).
  // Individual removed mappings are restored from inside the drawer.
  {
    key: 'edi-chargecodemap',
    label: 'EDI Charge Code Mapping',
    singular: 'EDI Charge Code Mapping',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-charge-codes',
    searchPlaceholder: 'Search EDI charge code mappings…',
    idKey: 'edihandle',
    softDelete: 'active',
    softDeleteInvert: true,
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 200, editable: false },
      { key: 'billtoname', label: 'Bill To', titleCase: true, width: 260, editable: false },
      { key: 'mapped_count', label: 'Line Items', type: 'number', align: 'right', width: 90, editable: false },
      // Blank codes are a real configuration ("send no code"), so the count is shown
      // rather than left to look like unset rows.
      { key: 'blank_count', label: 'Blank Codes', type: 'number', align: 'right', width: 100, editable: false },
      { key: 'code_list', label: 'Charge Codes', width: 320, editable: false },
    ],
  },
  // Internal EDI 210 Partners (cms.edi_invoice_x12_partners) — Integrations/EDI.
  // The four partners gwi360 builds an X12 210 for itself, rather than handing a CSV to
  // Kleinschmidt: their ISA/GS identity, file-name shape and transport endpoint. V1 keeps
  // all of it as globals at the top of each `do*` sub in invoiceedi.pl.
  //
  // NO ADD BUTTON: a row is inert without a Builder, and a builder is Python — the
  // segment layout of a 210 is not configuration. Bucket Key is locked after create for
  // the same reason it is on the server: it is the join to the pending-invoice board.
  //
  // Password Env Var holds the NAME of an environment variable, never a password.
  {
    key: 'edi-invoice-x12-partners',
    label: 'Internal EDI 210 Partners',
    singular: 'Internal EDI 210 Partner',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-invoice-x12-partners',
    searchPlaceholder: 'Search internal EDI 210 partners…',
    createLocked: true, // a row is inert without a Builder, and a builder is Python
    fields: [
      { key: 'bucketkey', label: 'Bucket Key', width: 120, createOnly: true },
      { key: 'label', label: 'Partner', width: 150 },
      { key: 'edihandle', label: 'EDI Handle', width: 130 },
      { key: 'sendscript', label: 'Send Script', width: 170 },
      { key: 'builder', label: 'Builder', width: 100 },
      { key: 'isa_sender_qual', label: 'ISA Sender Qual', width: 120 },
      { key: 'isa_sender_id', label: 'ISA Sender ID', width: 130 },
      { key: 'isa_receiver_qual', label: 'ISA Receiver Qual', width: 130 },
      { key: 'isa_receiver_id', label: 'ISA Receiver ID', width: 140 },
      { key: 'isa_version', label: 'ISA Version', width: 100 },
      { key: 'gs_version', label: 'GS Version', width: 100 },
      { key: 'segment_terminator', label: 'Segment Terminator', width: 130 },
      { key: 'filename_pattern', label: 'File Name Pattern', width: 220 },
      { key: 'file_extension', label: 'File Extension', width: 110 },
      { key: 'transport', label: 'Transport', width: 100 },
      { key: 'host', label: 'Host', width: 220 },
      { key: 'host_nonprod', label: 'Host (Non-Production)', width: 220 },
      { key: 'port', label: 'Port', type: 'number', align: 'right', width: 80 },
      { key: 'remotedir', label: 'Remote Directory', width: 180 },
      { key: 'remotedir_nonprod', label: 'Remote Directory (Non-Production)', width: 200 },
      { key: 'username', label: 'Username', width: 140 },
      { key: 'password_env', label: 'Password Env Var', width: 200 },
      { key: 'send_from_nonprod', label: 'Send From Non-Production', type: 'yn', width: 170 },
    ],
  },
  // EDI Invoice Rules (cms.edi_invoice_rules, migration 441) — Integrations/EDI. The
  // table form of V1's lib_invoice.pl::validateinvoice, so that "stop requiring a PO for
  // this customer" is a checkbox rather than a deploy.
  //
  // rule_key is createOnly: it is what logs, the Errors column and support tickets refer
  // to, and it is never reused. check_type is a fixed list because each one needs an
  // evaluator behind it — a new TYPE is code, a new RULE of an existing type is a row.
  //
  // A blank Applies Bill Tos means EVERY bill-to (six rules are global), which is why
  // the column is labelled to say so rather than left to be guessed at.
  {
    key: 'edi-invoice-rules',
    label: 'EDI Invoice Rules',
    singular: 'EDI Invoice Rule',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-invoice-rules',
    searchPlaceholder: 'Search rules by key, message or bill-to…',
    fields: [
      { key: 'rule_key', label: 'Rule Key', width: 200, required: true, createOnly: true, maxLength: 64 },
      { key: 'active', label: 'Active', type: 'yn', width: 70, defaultValue: 'Y' },
      { key: 'severity', label: 'Severity', type: 'select', width: 90,
        options: ['error', 'warning'], defaultValue: 'error', clearable: false },
      { key: 'message', label: 'Message', width: 280, required: true, maxLength: 255 },
      { key: 'applies_billtos', label: 'Applies Bill Tos (blank = all)', width: 200 },
      { key: 'excludes_billtos', label: 'Excludes Bill Tos', width: 160 },
      { key: 'applies_edihandles', label: 'Applies EDI Handles', width: 170 },
      { key: 'scope', label: 'Scope', type: 'select', width: 100,
        options: ['invoice', 'lineitem'], defaultValue: 'invoice', clearable: false },
      { key: 'check_type', label: 'Check Type', type: 'select', width: 170, required: true,
        clearable: false,
        options: ['field_required', 'field_forbidden', 'any_of_required',
                  'weight_required', 'invoice_count_max', 'lineitem_allowed',
                  'lineitem_forbidden', 'lineitem_field_required', 'lineitem_aggregate',
                  'custom'] },
      { key: 'field_name', label: 'Field', width: 150 },
      { key: 'compare_value', label: 'Compare Value', width: 150 },
      { key: 'value_list', label: 'Value List', width: 240, fullWidth: true },
      { key: 'gate_movetype_in', label: 'Only Move Types', width: 140 },
      { key: 'gate_movetype_not_in', label: 'Except Move Types', width: 150 },
      { key: 'gate_invoice_class', label: 'Invoice Class', type: 'select', width: 120,
        options: ['', 'MAIN', 'ACCESSORIAL'] },
      { key: 'gate_template_not_in', label: 'Except Templates', width: 200 },
      { key: 'gate_skip_on_resend', label: 'Skip On Resend', type: 'yn', width: 120, defaultValue: 'N' },
      // Three gates added with the evaluator, when three of V1's conditions turned out
      // to have no column. Blank means "don't care", which is why these are selects with
      // an empty option rather than Y/N toggles.
      { key: 'gate_all_accessorial', label: 'Only When All Accessorial', type: 'select',
        width: 180, options: ['', 'Y', 'N'] },
      { key: 'gate_has_edi_order', label: 'Only When EDI Order', type: 'select',
        width: 160, options: ['', 'Y', 'N'] },
      { key: 'gate_has_lineitem_outside', label: 'Only If A Line Outside', width: 190 },
      { key: 'notes', label: 'Notes', width: 300, fullWidth: true },
      { key: 'sort_order', label: 'Sort', type: 'number', align: 'right', width: 70 },
    ],
  },
  // EDI Document Setup (cms.edi_document_setup) — Integrations/EDI. Master +
  // edi_document_setup_lines (Document Type / Translation Code) subtable, edited via
  // the bespoke EdiDocumentSetupEditDrawer (+ the /edi-document-setup lines endpoint).
  // billtolist/customerlist/brokerlist are comma-delimited recno lists on the master;
  // the grid shows them expanded to names (computed billtoname/customername/brokername
  // via FIND_IN_SET), and the drawer edits them as name multiselects. Automated is a
  // BoolPill. Standard `deleted` soft-delete.
  {
    key: 'edi-document-setup',
    label: 'EDI Document Setup',
    singular: 'EDI Document Setup',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-document-setup',
    searchPlaceholder: 'Search EDI document setups…',
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 150, editable: false },
      { key: 'cmscode', label: 'CMS Code', width: 100, editable: false },
      { key: 'billtoname', label: 'Bill Tos', titleCase: true, width: 180, editable: false },
      { key: 'customername', label: 'Customers', titleCase: true, width: 180, editable: false },
      { key: 'brokername', label: 'Brokers', titleCase: true, width: 150, editable: false },
      { key: 'automated', label: 'Automated?', type: 'yn', width: 90, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // EDI Equipment Map (cms.edi_equipmentmap) — Integrations/EDI. Flat lookup mapping
  // an ISO code to a Size + Equipment Type. Generic field-spec drawer; standard
  // `deleted` soft-delete. ISO Code required + create-only; Size / Type are selects
  // over the shared EQUIPMENT_SIZES / EQUIPMENT_TYPES catalogues (orderFieldOptions).
  {
    key: 'edi-equipmentmap',
    label: 'EDI Equipment Map',
    singular: 'EDI Equipment Map',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-equipment-map',
    searchPlaceholder: 'Search EDI equipment maps…',
    fields: [
      { key: 'isocode', label: 'ISO Code', required: true, createOnly: true, maxLength: 10, width: 90 },
      { key: 'size', label: 'Size', type: 'select', options: [...EQUIPMENT_SIZES], width: 80 },
      { key: 'equiptype', label: 'Type', type: 'select', options: [...EQUIPMENT_TYPES], width: 180 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // EDI Events (cms.edi_events + cms.edi_events_archive) — Integrations/EDI. A
  // read-only combined view: main events (current orders) plus, behind the Show
  // Archived toggle, the archive (all other events). The bespoke EdiEventsEditDrawer
  // marks a MAIN event sent/unsent (sentdatetime) or deleted; archive rows are fully
  // locked (read-only). createLocked (events aren't added here); showArchivedToggle
  // folds in the archive; standard `deleted` soft-delete (Show Deleted filters both).
  {
    key: 'edi-events',
    label: 'EDI Events',
    singular: 'EDI Event',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-events',
    searchPlaceholder: 'Search by Pro #, EDI Handle, or Event…',
    createLocked: true,
    showArchivedToggle: true,
    // Clearing the sent marker is what makes the EDI script pick an event up again —
    // the same write the pro's EDI history calls Retrigger. This grid mixes live and
    // archived rows, and the two need different writes, so each goes out under its own
    // source (see sourceBy); the confirm names both counts before anything happens.
    bulkRetrigger: {
      label: 'Retrigger all in view',
      confirm: 'Retrigger these events so the EDI script sends them again?',
      sourceBy: {
        column: 'source',
        map: {
          main: { source: 'edievents', noun: 'live event' },
          archive: { source: 'edieventsarchive', noun: 'archived event (lifted back into the live table)' },
        },
      },
    },
    serverSearch: true,
    serverSearchLimit: 10000,
    // Pasted lists go to the SERVER on these columns. The grid holds the most recent
    // 10,000 of ~981k live + ~16.7M archived rows, so matching a pasted list against
    // the loaded page reported confident misses for anything older — pasting 137 pros
    // came back empty until a search term happened to pull those rows onto the page.
    // Mirrors TableCfg.list_filter_columns, which is authoritative.
    listFilterColumns: ['pronumber', 'edihandle', 'event', 'recno'],
    fields: [
      { key: 'source', label: 'Source', titleCase: true, width: 80, editable: false },
      { key: 'edihandle', label: 'EDI Handle', width: 130, editable: false },
      { key: 'pronumber', label: 'Pro Number', proLink: true, width: 120, editable: false },
      { key: 'event', label: 'Event', width: 120, editable: false },
      { key: 'eventdatetime', label: 'Event Date', width: 140, editable: false },
      { key: 'sentdatetime', label: 'Sent Date', width: 140, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI Inventory Adjustments (cms.edi_inventoryadj) — Integrations/EDI. Same shape as
  // EDI Events but with NO archive table: a read-only, server-searched view of the
  // inventory-adjustment EDI transactions. The bespoke EdiInventoryAdjEditDrawer marks a
  // row sent/unsent (edisentdatetime) or deleted; everything else is read-only.
  // createLocked (these are generated by EDI, not added here). The grid's Product column
  // shows the raw productrecno (V1 parity); the drawer shows the joined description.
  {
    key: 'edi-inventory-adj',
    label: 'EDI Inventory Adjustments',
    singular: 'EDI Inventory Adjustment',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-inventory-adjustments',
    searchPlaceholder: 'Search by Pro #, Movement Type, Batch #, or Product…',
    createLocked: true,
    serverSearch: true,
    serverSearchLimit: 10000,
    fields: [
      { key: 'pronumber', label: 'Pro Number', proLink: true, width: 120, editable: false },
      { key: 'rcvrecno', label: 'Rcv Recno', width: 100, editable: false },
      { key: 'movementtype', label: 'Movement Type', width: 130, editable: false },
      { key: 'productrecno', label: 'Product', width: 100, editable: false },
      { key: 'batchnumber', label: 'Batch No.', width: 110, editable: false },
      { key: 'qty', label: 'Qty', width: 80, align: 'right', editable: false },
      { key: 'uom', label: 'Uom', width: 90, editable: false },
      { key: 'eventdatetime', label: 'Event Date', width: 150, editable: false },
      { key: 'edisentdatetime', label: 'Sent Date', width: 150, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI Pickup Location Mapping (cms.edi_pickuplocationmap) — Integrations/EDI. Per
  // (EDI handle, SCAC[, delivery]) pickup override applied when a pending EDI order is
  // created into a pro: a match forces that pickup and LOCKS the field on the create
  // form; no match falls back to comparing the template's pickup with the EDI's.
  // Delivery Location is a MATCH QUALIFIER, not an output — blank means "any delivery".
  // SCAC accepts ALL (any SCAC for the handle); an exact SCAC outranks it. Generic
  // drawer (same shape as SCAC Locations): two selects + two location lookups.
  {
    key: 'edi-pickuplocationmap',
    label: 'EDI Pickup Location Mapping',
    singular: 'EDI Pickup Location Mapping',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-pickup-locations',
    searchPlaceholder: 'Search by EDI Handle, SCAC, or location…',
    fields: [
      { key: 'edihandle', label: 'EDI Handle', type: 'select',
        optionsEndpoint: '/cms-lookups/edi-order-handles', required: true,
        width: 150, fullWidth: true },
      { key: 'scac', label: 'SCAC', type: 'select',
        optionsEndpoint: '/cms-lookups/scacs?include_all=true', required: true,
        width: 100, fullWidth: true },
      { key: 'pickuplocation', label: 'Pickup Location', type: 'lookup', lookupTarget: 'location',
        required: true, fkZeroBlank: true, refNameKey: 'pu_lname', deletedKey: 'pudeleted',
        inTable: false, fullWidth: true },
      { key: 'pu_lname', label: 'Pickup', titleCase: true, width: 200,
        deletedKey: 'pudeleted', editable: false },
      { key: 'deliverylocation', label: 'Delivery Location (optional)', type: 'lookup',
        lookupTarget: 'location', fkZeroBlank: true, refNameKey: 'dl_lname',
        deletedKey: 'dldeleted', inTable: false, fullWidth: true },
      { key: 'dl_lname', label: 'Delivery', titleCase: true, width: 200,
        deletedKey: 'dldeleted', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI Locations (cms.edi_locations) — Integrations/EDI. ~22k partner locations received
  // over EDI, each mappable to a CMS location. Read-only + server-searched; the bespoke
  // EdiLocationsEditDrawer edits only Location Map (reusable LocationMapField — search
  // existing or Add New with dedup/undelete), Terminal (rail), Auto Accept + Deleted.
  // createLocked (locations arrive via EDI). Grid shows the raw locationcode (V1 parity);
  // the drawer's Location Map shows the mapped location name.
  {
    key: 'edi-locations',
    label: 'EDI Locations',
    singular: 'EDI Location',
    notice: AUTO_ACCEPT_CAUTION,
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-locations',
    searchPlaceholder: 'Search by EDI Handle, Name, Address, City, or Partner Code…',
    createLocked: true,
    serverSearch: true,
    serverSearchLimit: 10000,
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 120, editable: false },
      { key: 'name', label: 'Name', width: 200, editable: false },
      { key: 'address1', label: 'Address', width: 170, editable: false },
      { key: 'address2', label: 'Address 2', width: 120, editable: false },
      { key: 'city', label: 'City', width: 120, editable: false },
      { key: 'state', label: 'State', width: 60, align: 'center', editable: false },
      { key: 'zip', label: 'Zip', width: 70, editable: false },
      { key: 'country', label: 'Country', width: 70, editable: false },
      { key: 'locationcode', label: 'Location Map', width: 100, deletedKey: 'locationdeleted', editable: false },
      { key: 'rail', label: 'Rail', width: 80, editable: false },
      { key: 'partnercode', label: 'Partner Code', width: 100, editable: false },
      { key: 'autoaccept', label: 'Auto Accept', width: 90, align: 'center', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI Monitoring Master (cms.edimaster) — Integrations/EDI. Small (~350 rows) config
  // table: per (EDI handle, monitored table) notification settings. Uses the GENERIC
  // engine drawer (no bespoke component) via showReadOnlyContext — the handle/table/
  // created/last-run fields render as a read-only Details block above the editable
  // inputs. Editable: Notification Days (positive int), Type of Days (Business/Calendar),
  // Comments (textarea), Email (comma-delimited, validated); nothing required. No create
  // (createLocked). Small enough to load fully (no serverSearch).
  {
    key: 'edimaster',
    label: 'EDI Monitoring Master',
    singular: 'EDI Monitoring Entry',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-monitoring-master',
    searchPlaceholder: 'Search by Table Name, EDI Handle, or User…',
    createLocked: true,
    showReadOnlyContext: true,
    fields: [
      { key: 'tablename', label: 'Table Name', width: 140, editable: false },
      { key: 'edihandle', label: 'EDI Handle', width: 100, editable: false },
      { key: 'createddatetime', label: 'Created Date/Time', width: 150, editable: false },
      { key: 'lastrunuser', label: 'Last Run By User', width: 120, editable: false },
      { key: 'lastrundatetime', label: 'Last Run Date/Time', width: 150, editable: false },
      { key: 'notificationdays', label: 'Notification Days', type: 'number', integer: true, width: 120 },
      { key: 'ndaystype', label: 'Type of Days', type: 'select', width: 120, options: [
        { value: 'business', label: 'Business' },
        { value: 'calendar', label: 'Calendar' },
      ] },
      { key: 'comments', label: 'Comments', type: 'textarea', fullWidth: true, width: 200 },
      { key: 'email', label: 'Email', type: 'textarea', email: true, fullWidth: true, width: 160 },
    ],
  },
  // EDI Orders (cms.edi_orders) — Integrations/EDI. ~860k inbound EDI order records.
  // Read-only + server-searched grid; the bespoke EdiOrdersEditDrawer FETCHES the full
  // record (SCAC/broker/ports/hazmat/…) and applies conditional edits (clear pronumber
  // when set; else movetype / clear template / delete+reason / clear accept-reject).
  // createLocked (records arrive via EDI). Grid shows blanked pronumber for unset orders.
  {
    key: 'edi-orders',
    label: 'EDI Orders',
    singular: 'EDI Order',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-orders',
    searchPlaceholder: 'Search by Pro #, Container, BOL, Booking, PO, Ref #, or Bill To…',
    createLocked: true,
    serverSearch: true,
    serverSearchLimit: 10000,
    fields: [
      { key: 'billtoname', label: 'Bill To', width: 140, deletedKey: 'billtodeleted', editable: false },
      { key: 'customername', label: 'Customer', titleCase: true, width: 140, deletedKey: 'custdeleted', editable: false },
      { key: 'pronumber', label: 'Pro #', proLink: true, width: 90, editable: false },
      { key: 'movetype', label: 'Move Type', width: 100, editable: false },
      { key: 'container', label: 'Container', width: 110, editable: false },
      { key: 'bol', label: 'BOL', width: 110, editable: false },
      { key: 'booking', label: 'Booking', width: 100, editable: false },
      { key: 'billtorefno', label: 'Billing Ref #', width: 110, editable: false },
      { key: 'po', label: 'PO', width: 100, editable: false },
      { key: 'pickupname', label: 'Pickup', width: 140, editable: false },
      { key: 'deliveryname', label: 'Deliver To', width: 140, editable: false },
      { key: 'terminatename', label: 'Terminate', width: 140, editable: false },
      { key: 'template', label: 'Template', width: 90, editable: false },
      { key: 'processdatetime', label: 'Receive Date', width: 140, editable: false },
      { key: 'editype', label: 'Type', width: 70, editable: false },
      { key: 'acceptreject', label: 'A/R', width: 60, align: 'center', editable: false },
      { key: 'edifilename', label: 'EDI File', width: 220, editable: false },
      { key: 'nas_edifilename', label: 'NAS EDI File', width: 220, editable: false },
    ],
  },
  // EDI Update Files (cms.edi_updatefiles) — Integrations/EDI. ~726k EDI update records.
  // The ONLY editable field is Deleted, so this is CONFIG-ONLY (generic drawer, no bespoke
  // component): showReadOnlyContext renders EDI Handle / Pro / dates / event as a read-only
  // Details block, and the standard soft-delete toggle does the edit. createLocked (records
  // arrive via EDI) + serverSearch (500-cap). Pro # proLink; pronumber 0 blanked.
  {
    key: 'edi-updatefiles',
    label: 'EDI Update Files',
    singular: 'EDI Update File',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-update-files',
    searchPlaceholder: 'Search by EDI Handle, Pro #, or Event…',
    createLocked: true,
    serverSearch: true,
    serverSearchLimit: 10000,
    showReadOnlyContext: true,
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 130, editable: false },
      { key: 'pronumber', label: 'Pro Number', proLink: true, width: 110, editable: false },
      { key: 'updatedatetime', label: 'Update Date/Time', width: 150, editable: false },
      { key: 'event', label: 'Event Type', width: 90, editable: false },
      { key: 'eventdatetime', label: 'Event Date/Time', width: 150, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI Port Mapping (cms.edi_portmap) — Integrations/EDI. 375 rows mapping an EDI-supplied
  // port name to a CMS port. NO deleted column (noSoftDelete) + createLocked. Generic
  // drawer: EDI Handle / EDI Port Name are read-only context (showReadOnlyContext) and the
  // only edit is Port, a `type: 'lookup'` async picker over cms.ports (portcode =
  // ports.recno). Grid's Port column (pport) is a computed subquery to cms.ports and
  // doubles as the picker's refNameKey so the mapped port shows on open.
  {
    key: 'edi-portmap',
    label: 'EDI Port Mapping',
    singular: 'EDI Port Mapping',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-port-mapping',
    searchPlaceholder: 'Search by EDI Handle, EDI Port Name, or Port…',
    createLocked: true,
    noSoftDelete: true,
    showReadOnlyContext: true,
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 260, editable: false },
      { key: 'port', label: 'EDI Port Name', width: 260, editable: false },
      { key: 'pport', label: 'Port', width: 260, deletedKey: 'portdeleted', editable: false },
      { key: 'portcode', label: 'Port', type: 'lookup', lookupTarget: 'port',
        fkZeroBlank: true, refNameKey: 'pport', deletedKey: 'portdeleted', inTable: false, fullWidth: true },
    ],
  },
  // EDI SCAC Mapping (cms.edi_scacmap) — Integrations/EDI. 88 rows mapping an EDI SS-line
  // text to a CMS SCAC. NO deleted column (noSoftDelete) + createLocked. CONFIG-ONLY
  // (generic drawer): showReadOnlyContext renders EDI Handle / SS Line read-only, and the
  // only edit is the SCAC select over the shared /cms-lookups/scacs list. No bespoke drawer.
  {
    key: 'edi-scacmap',
    label: 'EDI SCAC Mapping',
    singular: 'EDI SCAC Mapping',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-scac-mapping',
    searchPlaceholder: 'Search by EDI Handle, SS Line, or SCAC…',
    createLocked: true,
    noSoftDelete: true,
    showReadOnlyContext: true,
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 300, editable: false },
      { key: 'scactext', label: 'SS Line', width: 300, editable: false },
      { key: 'scac', label: 'SCAC Mapping', columnLabel: 'SCAC', type: 'select', optionsEndpoint: '/cms-lookups/scacs', width: 120, fullWidth: true, deletedKey: 'scacdeleted' },
    ],
  },
  // EDI Invoices (cms.edi_sentinvoices) — Integrations/EDI. ~950k sent-EDI-invoice records.
  // Read-only except the Re-Send / Do Not Send Y/N flags (EDI Sent Date is locked — mark
  // Re-Send instead). CONFIG-ONLY (generic drawer): showReadOnlyContext renders the read-only
  // fields incl. the drawer-only Sent By / File Name (inTable:false). createLocked +
  // noSoftDelete (no deleted column) + serverSearch (500-cap). Bill To via a two-hop join.
  {
    key: 'edi-sentinvoices',
    label: 'EDI Invoices',
    singular: 'EDI Invoice',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-invoices',
    searchPlaceholder: 'Search by EDI Handle, either Invoice #, or ISA #…',
    createLocked: true,
    noSoftDelete: true,
    serverSearch: true,
    serverSearchLimit: 10000,
    // `pronumber` is not a column of edi_sentinvoices — it is aggregated from the
    // invoice history, and its Match List is answered server-side by an EXISTS. That is
    // the point of listing it: a user holding 180 PROs rather than 180 invoice numbers
    // can still find these rows.
    listFilterColumns: ['invoicenumber', 'cmsinvoicenumber', 'isa_cntr', 'script', 'pronumber'],
    showReadOnlyContext: true,
    // The reason this table gets an action bar: re-sending a batch of invoices is
    // routine and the batches are big. Filter by pasted invoice list, then one click.
    bulkRetrigger: {
      source: 'edisentinvoices',
      label: 'Set all in view to Re-Send',
      confirm: 'Mark these invoices to re-send on the next EDI run?',
    },
    fields: [
      { key: 'script', label: 'EDI Handle', width: 160, editable: false },
      { key: 'cname', label: 'Bill To', width: 220, editable: false },
      { key: 'isa_cntr', label: 'ISA #', width: 100, editable: false },
      // TWO invoice numbers, because the EDI and CMS genuinely disagree on the string.
      // What went out on the EDI is the partner's own format: bare for most, bill-to
      // tagged for Albemarle/Ketjen/Maxam/Sailun ('KETJEN-3026877'), and for ATD not an
      // invoice number at all but the PRO plus accessorial ('ATD INC-4720417-2' = CMS
      // invoice 2930964). Anyone researching one of these holds the CMS number or the
      // PRO, so both are columns AND both take a pasted list — matching is exact, and
      // against the EDI string alone a pasted list of CMS numbers matched nothing.
      { key: 'invoicenumber', label: 'Invoice # (EDI)', width: 150, editable: false },
      // invoice_history.masterpro, via edi_sentinvoices.invhistoryrecno.
      { key: 'cmsinvoicenumber', label: 'Invoice # (CMS)', width: 140, editable: false },
      // Plural because an invoice history can cover many PROs (up to 402 on prod, mean
      // 1.52) — they arrive as one comma-separated cell rather than as duplicated rows,
      // so the grid keeps one row per invoice and Re-Send still counts invoices.
      // multiValue is what makes a pasted PRO list match them: the server answers with
      // an EXISTS over every pro on the invoice, so the grid must match per pro too, or
      // it discards each multi-pro invoice the server just returned.
      { key: 'pronumber', label: 'Pro Number(s)', width: 130, editable: false, multiValue: true },
      { key: 'invoicedate', label: 'Invoice Date', width: 110, editable: false },
      { key: 'createdatetime', label: 'EDI Sent Date/Time', columnLabel: 'EDI Sent Date', width: 150, editable: false },
      { key: 'userid', label: 'Sent By', inTable: false, editable: false },
      { key: 'filename', label: 'File Name', inTable: false, editable: false },
      { key: 'sent', label: 'Sent', type: 'yn', width: 70, align: 'center', editable: false },
      { key: 'resend', label: 'Re-Send', type: 'yn', width: 90, align: 'center' },
      { key: 'donotsend', label: 'Do Not Send', type: 'yn', width: 100, align: 'center' },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // EDI Invoicing Setup (cms.edi_invoicehistoryrecno) — Integrations/EDI. 106 rows, one per
  // Bill To (PK=billto, no recno). Fully editable + creatable (config table). Bespoke
  // EdiInvoiceSetupEditDrawer: enriched Bill To lookup (locked in edit — it's the key),
  // Starting Invoice # (masterpro -> invoicehistoryrecno server-side), Required EDI Handle
  // (edi_orders handles dropdown), Move Type List (multiselect). Writes via the billto-keyed
  // /edi-invoice-setup router; grid is the read-only engine list_sql. idKey='billto',
  // noSoftDelete, Add allowed (not createLocked).
  {
    key: 'edi-invoice-setup',
    label: 'EDI Invoicing Setup',
    singular: 'EDI Invoicing Setup',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/edi-invoicing-setup',
    searchPlaceholder: 'Search by Bill To, Starting Invoice #, EDI Handle, or Move Type…',
    idKey: 'billto',
    noSoftDelete: true,
    fields: [
      { key: 'cname', label: 'Bill To', width: 300, editable: false },
      { key: 'masterpro', label: 'Starting Invoice #', width: 150, editable: false },
      { key: 'edihandle204', label: 'Required EDI Handle', width: 160, editable: false },
      { key: 'invmovetype', label: 'Move Type List', width: 300, editable: false },
    ],
  },
  // Kleinschmidt Partners (cms.kleinschmidt_partners) — a partner's EDI configuration
  // (ISA/GS ID, SFTP folder, Bill To/Customer + additional lists, message-type toggles).
  // Bespoke KleinschmidtPartnersEditDrawer (Bill To/Customer typeaheads + a
  // self-referential Invoicing Order EDI Handle select, all served by
  // /kleinschmidt/form-options). Grid shows the computed cname/custname. edihandle is
  // the UNIQUE key (dup-checked). Standard `deleted` soft-delete.
  {
    key: 'kleinschmidt-partners',
    label: 'Kleinschmidt Partners',
    singular: 'Kleinschmidt Partner',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/kleinschmidt-partners',
    searchPlaceholder: 'Search Kleinschmidt partners…',
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 150, editable: false },
      { key: 'isagsid', label: 'ISA/GS ID', width: 120, editable: false },
      { key: 'cname', label: 'Bill To', titleCase: true, width: 200, deletedKey: 'billtodeleted', editable: false },
      { key: 'custname', label: 'Customer', titleCase: true, width: 200, deletedKey: 'custdeleted', editable: false },
      // Outbound-204 carrier targeting (migration 480). carrierdeleted inverts
      // carrierprofile.active, which is how "gone" is spelled in the highway schema.
      { key: 'carriername', label: 'Carrier', titleCase: true, width: 200, deletedKey: 'carrierdeleted', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Kleinschmidt Event Sets (cms.kleinschmidt_events) — which EDI events fire for a given
  // (edihandle, targets) setup. Bespoke KleinschmidtEventsEditDrawer (edihandle + cmscode
  // selects; Bill To/Customer/Broker/SCAC multiselects that persist as comma-delimited
  // list columns; ~31 Y/N event toggles). No joins — the grid shows the raw list columns.
  // edihandle is the UNIQUE key (dup-checked). Standard `deleted` soft-delete.
  {
    key: 'kleinschmidt-events',
    label: 'Kleinschmidt Event Sets',
    singular: 'Kleinschmidt Event Set',
    group: 'integrations',
    subgroup: 'EDI',
    route: '/data-management/kleinschmidt-events',
    searchPlaceholder: 'Search Kleinschmidt event sets…',
    fields: [
      { key: 'edihandle', label: 'EDI Handle', width: 150, editable: false },
      { key: 'cmscode', label: 'CMS Code', width: 100, editable: false },
      { key: 'billtonames', label: 'Bill Tos', titleCase: true, width: 200, editable: false },
      { key: 'customernames', label: 'Customers', titleCase: true, width: 200, editable: false },
      { key: 'brokernames', label: 'Brokers', titleCase: true, width: 180, editable: false },
      { key: 'scaclist', label: 'SCACs', width: 120, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Melrose Template Mapping (cms.melrose_templatemap) — Integrations → Melrose. Bespoke
  // MelroseTemplateMapEditDrawer (Customer/Bill To/Consignee/Pickup lookups, Move Type/
  // SCAC/Equip Type selects, Max Lbs, the shared Template search, Master Pro toggle).
  // Grid shows the joined names (computed) + Max Lbs blank when 0. Bill To editable only
  // for customercodes 5925/5860; only one of Pickup/SCAC/Equip Type allowed.
  {
    key: 'melrose-templatemap',
    label: 'Melrose Template Mapping',
    singular: 'Melrose Template Map',
    group: 'integrations',
    subgroup: 'Melrose',
    route: '/data-management/melrose-template-mapping',
    searchPlaceholder: 'Search Melrose template maps…',
    fields: [
      { key: 'custname', label: 'Customer', titleCase: true, align: 'center', width: 150, idKey: 'customercode', deletedKey: 'custdeleted', editable: false },
      { key: 'consigneename', label: 'Consignee', titleCase: true, align: 'center', width: 150, idKey: 'consignee', deletedKey: 'consigneedeleted', editable: false },
      { key: 'billtoname', label: 'Bill To', titleCase: true, align: 'center', width: 120, idKey: 'billto', deletedKey: 'billtodeleted', editable: false },
      { key: 'movetype', label: 'Move Type', align: 'center', width: 90, editable: false },
      { key: 'pickupname', label: 'Pickup Location', titleCase: true, align: 'center', width: 130, idKey: 'pickup', deletedKey: 'pickupdeleted', editable: false },
      { key: 'scac', label: 'SCAC', align: 'center', width: 70, deletedKey: 'scacdeleted', editable: false },
      { key: 'equiptype', label: 'Equip Type', align: 'center', width: 110, editable: false },
      { key: 'maxlbsdisplay', label: 'Max Lbs', align: 'center', width: 80, editable: false },
      { key: 'template', label: 'Template', align: 'center', width: 180, deletedKey: 'tpldeleted', approvedKey: 'tplapproved', editable: false },
      { key: 'tplapproved', label: 'Template Approved', type: 'yn', align: 'center', width: 140, editable: false },
      { key: 'singlemaster', label: 'Master Pro', type: 'yn', align: 'center', width: 90, editable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Melrose Location Map (cms.melrose_locationmap) — Integrations → Melrose. An
  // open-text Melrose location name + a CMS location picker (locationcode,
  // /cms-lookups/locations). The grid shows the mapped CMS location name (computed lname)
  // + the raw code. Both required. Standard `deleted` soft-delete.
  {
    key: 'melrose-locationmap',
    label: 'Melrose Location Mapping',
    singular: 'Melrose Location Mapping',
    group: 'integrations',
    subgroup: 'Melrose',
    route: '/data-management/melrose-location-mapping',
    searchPlaceholder: 'Search Melrose location mappings…',
    fields: [
      { key: 'locationname', label: 'Melrose Location', width: 200, required: true, fullWidth: true },
      // Display-only join of `locationcode`; editable:false keeps it out of the drawer.
      { key: 'lname', label: 'CMS Location', titleCase: true, width: 200,
        deletedKey: 'locationdeleted', editable: false },
      { key: 'locationcode', label: 'CMS Location', columnLabel: 'Location Code', align: 'center', width: 100,
        type: 'select', optionsEndpoint: '/cms-lookups/locations', showDescriptions: true,
        showSelectedInfo: true, fkZeroBlank: true, required: true, fullWidth: true,
        refNameKey: 'lname', deletedKey: 'locationdeleted' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Melrose Bill To Mapping (cms.melrose_billtomap) — Integrations → Melrose. Twin of the
  // location map: an open-text Melrose bill-to name + a CMS bill-to picker (billto,
  // /cms-lookups/billtos). The grid shows the mapped CMS bill-to name (computed cname)
  // + the raw code. Both required; UNIQUE billtoname (dup-guard + reactivate). Standard
  // `deleted` soft-delete. Generic drawer.
  {
    key: 'melrose-billtomap',
    label: 'Melrose Bill To Mapping',
    singular: 'Melrose Bill To Mapping',
    group: 'integrations',
    subgroup: 'Melrose',
    route: '/data-management/melrose-billto-mapping',
    searchPlaceholder: 'Search Melrose bill to mappings…',
    fields: [
      { key: 'billtoname', label: 'Melrose Bill To', width: 200, required: true, fullWidth: true },
      // Display-only join of `billto`; editable:false keeps it out of the drawer.
      { key: 'cname', label: 'CMS Bill To', titleCase: true, width: 200,
        deletedKey: 'billtodeleted', editable: false },
      { key: 'billto', label: 'CMS Bill To', columnLabel: 'Bill To Code', align: 'center', width: 100,
        type: 'select', optionsEndpoint: '/cms-lookups/billtos', showDescriptions: true,
        showSelectedInfo: true, fkZeroBlank: true, required: true, fullWidth: true,
        refNameKey: 'cname', deletedKey: 'billtodeleted' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Melrose SCAC Mapping (cms.melrose_scacmap) — Integrations → Melrose. Config-only,
  // generic drawer: an open-text SS line name + a SCAC select (shared /cms-lookups/scacs).
  // No joins — grid shows both raw. Both required; UNIQUE melrosescac (dup-guard +
  // reactivate). Standard `deleted` soft-delete.
  {
    key: 'melrose-scacmap',
    label: 'Melrose SCAC Mapping',
    singular: 'Melrose SCAC Mapping',
    group: 'integrations',
    subgroup: 'Melrose',
    route: '/data-management/melrose-scac-mapping',
    searchPlaceholder: 'Search Melrose SCAC mappings…',
    fields: [
      { key: 'melrosescac', label: 'SS Line', required: true, maxLength: 100, width: 200 },
      { key: 'scac', label: 'SCAC Mapping', columnLabel: 'SCAC', type: 'select', required: true, optionsEndpoint: '/cms-lookups/scacs', width: 160, deletedKey: 'scacdeleted' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Melrose Equipment Mapping (cms.melrose_equipmentmap) — Integrations → Melrose.
  // Config-only, generic drawer: an open-text Size/Type + Size / Type / Chassis Type
  // selects sharing the order form's constant lists. No joins. Size/Type + Size + Type
  // required; UNIQUE sizetype (dup-guard + reactivate). Standard `deleted` soft-delete.
  {
    key: 'melrose-equipmentmap',
    label: 'Melrose Equipment Mapping',
    singular: 'Melrose Equipment Mapping',
    group: 'integrations',
    subgroup: 'Melrose',
    route: '/data-management/melrose-equipment-mapping',
    searchPlaceholder: 'Search Melrose equipment mappings…',
    fields: [
      { key: 'sizetype', label: 'Size/Type', required: true, maxLength: 100, width: 120 },
      { key: 'size', label: 'Size', type: 'select', required: true, options: [...EQUIPMENT_SIZES], width: 90 },
      { key: 'equiptype', label: 'Equip Type', columnLabel: 'Type', type: 'select', required: true, options: [...EQUIPMENT_TYPES], width: 160 },
      { key: 'chassistype', label: 'Chassis Type', type: 'select', options: [...CHASSIS_TYPES], width: 160 },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Melrose Orders (cms.melrose_orders) — Integrations → Melrose. A mostly read-only
  // import log (like EDI Orders); the only editable field is Processed (Y/N). Bespoke
  // MelroseOrdersEditDrawer: read-only record + Melrose deep-link, collapsible raw JSON,
  // errors textarea, and the Processed toggle. Grid shows joined customer/consignee names
  // + formatted dates (computed). Not creatable (createLocked); no soft-delete.
  {
    key: 'melrose-orders',
    label: 'Melrose Orders',
    singular: 'Melrose Order',
    group: 'integrations',
    subgroup: 'Melrose',
    route: '/data-management/melrose-orders',
    searchPlaceholder: 'Search Melrose orders…',
    createLocked: true,
    noSoftDelete: true,
    // Newest imports on top. The column renders a formatted date string, so the
    // grid sorts it with TanTable's dateAwareSort (see TablemaintPage) rather than
    // alphanumerically; the backend's list_order agrees so an untouched grid and
    // the raw payload are in the same order.
    defaultSort: [{ id: 'createdatetime', desc: true }],
    // The page exists to work the import failures, so it opens on them.
    nonBlankFilter: { column: 'errordetails', noun: 'errors' },
    fields: [
      { key: 'createdatetime', label: 'Create Date', width: 130, editable: false },
      { key: 'createdby', label: 'Created By', width: 90, editable: false },
      { key: 'custname', label: 'Customer', titleCase: true, width: 150, idKey: 'customercode', deletedKey: 'custdeleted', editable: false },
      { key: 'consignee', label: 'Consignee', titleCase: true, width: 150, idKey: 'consigneecode', deletedKey: 'consigneedeleted', editable: false },
      { key: 'movetype', label: 'Move Type', width: 90, editable: false },
      { key: 'mbl', label: 'BOL', width: 120, editable: false },
      { key: 'bookingnumber', label: 'Booking #', width: 100, editable: false },
      { key: 'billtorefno', label: 'Bill To Ref #', width: 100, editable: false },
      { key: 'processed', label: 'Processed', type: 'yn', align: 'center', width: 80, editable: false },
      { key: 'processdatetime', label: 'Process Date', width: 130, editable: false },
      { key: 'errordetails', label: 'Errors', width: 200, editable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Dal Tile Template Mapping (cms_daltile.templatemap — the new `daltile` schema)
  // — first Integrations table (Upload subgroup, where Dal Tile folds in). Bespoke
  // drawer (Dal-Tile RDC/port lookups + shared SCAC/rail/location + a template
  // search-as-you-type served by /daltile). Grid shows Pickup Location (computed
  // lname) rather than the raw pickup code; standard `deleted` soft-delete.
  {
    key: 'daltile-templatemap',
    label: 'Dal Tile Template Mapping',
    singular: 'Template Map',
    group: 'integrations',
    subgroup: 'Upload',
    route: '/data-management/dal-tile-template-mapping',
    searchPlaceholder: 'Search template maps…',
    fields: [
      { key: 'rdc', label: 'RDC', width: 70, align: 'center', editable: false },
      { key: 'portdestination', label: 'Port Destination', width: 120, editable: false },
      { key: 'carriername', label: 'SCAC', width: 70, editable: false },
      { key: 'template', label: 'Legal Template', width: 100, deletedKey: 'tpldeleted', editable: false },
      { key: 'overweighttemplate', label: 'Overweight Template', width: 130, deletedKey: 'owtpldeleted', editable: false },
      { key: 'rail', label: 'Rail', width: 90, editable: false },
      { key: 'pickupname', label: 'Pickup Location', titleCase: true, width: 160, idKey: 'pickupcode', deletedKey: 'pickupdeleted', editable: false },
    ],
  },
  // Dal Tile Carrier Mapping (cms.daltile_carrier_map) — Integrations/Upload. Generic
  // drawer: free-text Dal Tile Carrier + a `type: 'lookup'` carrier picker (stores the
  // highway.carrierprofile recno, shows truckername via a cross-schema computed column).
  // Search-only rather than a select — ~23,700 active carriers. Both required.
  // Grid shows the mapped carrier name; standard `deleted` soft-delete.
  {
    key: 'daltile-carrier-map',
    label: 'Dal Tile Carrier Mapping',
    singular: 'Carrier Map',
    group: 'integrations',
    subgroup: 'Upload',
    route: '/data-management/dal-tile-carrier-mapping',
    searchPlaceholder: 'Search carrier maps…',
    fields: [
      { key: 'carrier', label: 'Dal Tile Carrier', required: true, width: 160, fullWidth: true },
      { key: 'carriercode', label: 'Carrier', type: 'lookup', lookupTarget: 'carrier',
        required: true, fkZeroBlank: true, refNameKey: 'truckername', deletedKey: 'carrierdeleted',
        inTable: false, fullWidth: true },
      { key: 'truckername', label: 'Carrier', titleCase: true, width: 180, deletedKey: 'carrierdeleted', editable: false },
    ],
  },
  // ---------------------------------------------------------------------------
  // Home Depot (cms_homedepot) — the three tables the THD OTW import reads. V1
  // keeps them in the THD app's own menu (cmshomedepot/tablemaint.pl): "Template
  // Mapping", "IDC Locations", "OTW Non-GWI (Exceptions)". Labelled with the
  // customer prefix here because Data Management is one namespace — "DCs" is
  // already taken by cms.dc_map, which is a DIFFERENT table.
  // ---------------------------------------------------------------------------
  // Home Depot Template Mapping (cms_homedepot.templatemap) — (SCAC, Mode, IDC)
  // chooses the template the import creates from, plus the load/empty CY, rail and
  // dispatcher it stamps on the order. Bespoke drawer: preloaded selects for every
  // field plus the shared template search-as-you-type (reuses /daltile/template-search).
  // Options come from /thd/form-options. SCAC / Mode / IDC / Template required.
  //
  // THREE RED FLAGS, all of them real: the import's getTemplateMapping joins
  // `template.deleted='N'` and blanks a retired dispatcher, so a row with a deleted
  // template is invisible to it and a row with a retired dispatcher loses that value.
  // Measured on prod 2026-09-22: 258 of 314 live rows carry a dead template.
  {
    key: 'thd-templatemap',
    label: 'Home Depot Template Mapping',
    singular: 'Template Map',
    group: 'integrations',
    subgroup: 'Home Depot',
    route: '/data-management/home-depot-template-mapping',
    searchPlaceholder: 'Search template maps…',
    fields: [
      { key: 'scac', label: 'SCAC', required: true, width: 70, align: 'center', editable: false },
      { key: 'mode', label: 'Mode', required: true, width: 70, align: 'center', editable: false },
      { key: 'dcnumber', label: 'Final Destination (IDC)', columnLabel: 'IDC', required: true,
        width: 80, align: 'center', deletedKey: 'dcunmapped', editable: false },
      { key: 'dcname', label: 'IDC Name', titleCase: true, width: 160, editable: false },
      { key: 'loadcyname', label: 'Load CY', titleCase: true, width: 150, idKey: 'loadcyid',
        deletedKey: 'loadcydeleted', editable: false },
      { key: 'emptycyname', label: 'Empty CY', titleCase: true, width: 150, idKey: 'emptycyid',
        deletedKey: 'emptycydeleted', editable: false },
      { key: 'rail', label: 'Rail/Port Trace', columnLabel: 'Rail/Port', width: 90, align: 'center', editable: false },
      { key: 'dispatchername', label: 'Dispatcher', width: 130, idKey: 'dispatcher',
        deletedKey: 'dispatcherdeleted', editable: false },
      { key: 'template', label: 'Template', width: 90, align: 'center', deletedKey: 'tpldeleted', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Home Depot IDC Locations (cms_homedepot.dc) — the IDC numbers the template map
  // routes to, each mapped to the CMS location the dray runs to. Generic drawer:
  // Number + Name open text, Dray Location the shared /cms-lookups/locations select.
  // Number is the UNIQUE key and is joined BY VALUE from templatemap.dcnumber and
  // orderscurrent.dcnumber, so it is create-only; the backend refuses to delete an
  // IDC a live template-map row still routes to.
  {
    key: 'thd-dc',
    label: 'Home Depot IDC Locations',
    singular: 'IDC',
    group: 'integrations',
    subgroup: 'Home Depot',
    route: '/data-management/home-depot-idc-locations',
    searchPlaceholder: 'Search IDCs…',
    fields: [
      { key: 'number', label: 'Number', required: true, createOnly: true, maxLength: 20, width: 110 },
      { key: 'name', label: 'Name', maxLength: 100, width: 200 },
      { key: 'locationcode', label: 'Dray Location', columnLabel: 'Location Code', width: 90, align: 'center',
        type: 'select', optionsEndpoint: '/cms-lookups/locations', showDescriptions: true,
        showSelectedInfo: true, fkZeroBlank: true, required: true, fullWidth: true,
        refNameKey: 'lname', deletedKey: 'locdeleted' },
      { key: 'lname', label: 'Location', titleCase: true, width: 200, deletedKey: 'locdeleted', editable: false },
      { key: 'lcity', label: 'City', titleCase: true, width: 120, editable: false },
      { key: 'lstate', label: 'State', width: 55, align: 'center', editable: false },
      // How many live template-map rows route here — the "safe to delete?" answer,
      // and the same count the backend's delete guard reports when it refuses.
      { key: 'maprows', label: 'Template Maps', type: 'number', width: 100, align: 'right', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Home Depot OTW Non-GWI (cms_homedepot.exception) — the import's exclusion list.
  // An OTW row whose SCAC / Mode / Size appears here is somebody else's move and no
  // order is created for it. Generic drawer, two fields; V1 never guarded duplicates
  // so neither do we.
  {
    key: 'thd-exception',
    label: 'Home Depot OTW Non-GWI',
    singular: 'Exception',
    group: 'integrations',
    subgroup: 'Home Depot',
    route: '/data-management/home-depot-otw-non-gwi',
    searchPlaceholder: 'Search exceptions…',
    fields: [
      { key: 'code', label: 'Code', required: true, maxLength: 25, width: 120 },
      { key: 'type', label: 'Type', type: 'select', required: true, options: ['', 'SCAC', 'MODE', 'SIZE'],
        width: 100, align: 'center' },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Upload Equipment Mapping (cms.upload_equipmentmap) — Integrations → Upload. Maps a
  // customer's size/type code to CMS Size / Type / Chassis Type. Config-only, generic drawer:
  // Customer is a searchable select from /cms-lookups/customers (stores customercode; grid
  // shows the joined custname); Size/Type is open text; Size / Type / Chassis Type share the
  // order form's constant lists. Customer / Size/Type / Size / Type required (Chassis Type
  // optional). UNIQUE (customercode, sizetype) → dup-guard + reactivate. Standard soft-delete.
  {
    key: 'upload-equipmentmap',
    label: 'Upload Equipment Mapping',
    singular: 'Equipment Map',
    group: 'integrations',
    subgroup: 'Upload',
    route: '/data-management/upload-equipment-mapping',
    searchPlaceholder: 'Search upload equipment mappings…',
    fields: [
      { key: 'custname', label: 'Customer', titleCase: true, width: 220,
        deletedKey: 'custdeleted', editable: false },
      { key: 'customercode', label: 'Customer', type: 'select', required: true,
        optionsEndpoint: '/cms-lookups/customers', showDescriptions: true, showSelectedInfo: true,
        inTable: false, fullWidth: true, refNameKey: 'custname', deletedKey: 'custdeleted' },
      { key: 'sizetype', label: 'Size/Type', required: true, maxLength: 30, width: 90 },
      { key: 'size', label: 'Size', type: 'select', required: true, options: [...EQUIPMENT_SIZES], width: 80 },
      { key: 'equiptype', label: 'Equip Type', columnLabel: 'Type', type: 'select', required: true, options: [...EQUIPMENT_TYPES], width: 150 },
      { key: 'chassistype', label: 'Chassis Type', type: 'select', options: [...CHASSIS_TYPES], width: 150 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Upload Port Mapping (cms.upload_portmap) — Integrations → Upload. Maps a customer + inbound
  // port-destination name to a CMS port + a template-mapping flag. Generic drawer: Customer is
  // a preloaded select (/cms-lookups/customers), Port Name open text, Port a `type: 'lookup'`
  // async picker over cms.ports, Template Mapping Y/N. `pport` is computed but NOT in
  // list_columns, so the drawer hydrates to resolve the mapped port's name on open.
  // Customer + Port Name required; UNIQUE (customercode,
  // portdestination) → dup-guard + reactivate. Standard `deleted` soft-delete.
  {
    key: 'upload-portmap',
    label: 'Upload Port Mapping',
    singular: 'Port Map',
    group: 'integrations',
    subgroup: 'Upload',
    route: '/data-management/upload-port-mapping',
    searchPlaceholder: 'Search upload port mappings…',
    fields: [
      { key: 'custname', label: 'Customer', titleCase: true, width: 220, idKey: 'customercode', deletedKey: 'custdeleted', editable: false },
      { key: 'customercode', label: 'Customer', type: 'select', optionsEndpoint: '/cms-lookups/customers',
        showDescriptions: true, showSelectedInfo: true, fkZeroBlank: true, required: true,
        inTable: false, fullWidth: true, refNameKey: 'custname', deletedKey: 'custdeleted' },
      { key: 'portdestination', label: 'Port Name', columnLabel: 'Port Destination',
        required: true, width: 200, fullWidth: true },
      { key: 'portcode', label: 'Port', columnLabel: 'Port Code', type: 'lookup', lookupTarget: 'port',
        fkZeroBlank: true, refNameKey: 'pport', deletedKey: 'portdeleted', align: 'right', width: 100, fullWidth: true },
      { key: 'templatemapping', label: 'Template Mapping', type: 'yn', align: 'center', width: 120 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Upload Broker Mapping (cms.upload_brokermap, migration 475) — Integrations → Upload.
  // Maps a broker/forwarder NAME as spelled in a customer's upload file to a
  // cms.brokerforwarder record. Config-only, generic drawer: Customer and Broker are
  // preloaded selects (/cms-lookups/customers, /cms-lookups/brokers), Broker Name is open
  // text. All three required. UNIQUE (customercode, brokername) → dup-guard + reactivate.
  //
  // Replaces otw_dalt_updated.pl's hardcoded %brokerMap. The importer reads it for BOTH
  // the broker and the forwarder column and an unmapped name rejects the WHOLE FILE, so
  // each new Dal Tile forwarder previously needed a developer and a deploy. Aliases are
  // expected, not duplicates — Dal Tile spells HELLMANN four ways and all four map to the
  // same record.
  {
    key: 'upload-brokermap',
    label: 'Upload Broker Mapping',
    singular: 'Broker Map',
    group: 'integrations',
    subgroup: 'Upload',
    route: '/data-management/upload-broker-mapping',
    searchPlaceholder: 'Search upload broker mappings…',
    fields: [
      { key: 'custname', label: 'Customer', titleCase: true, width: 200, idKey: 'customercode',
        deletedKey: 'custdeleted', editable: false },
      { key: 'customercode', label: 'Customer', type: 'select', optionsEndpoint: '/cms-lookups/customers',
        showDescriptions: true, showSelectedInfo: true, fkZeroBlank: true, required: true,
        inTable: false, fullWidth: true, refNameKey: 'custname', deletedKey: 'custdeleted' },
      // The name as it arrives in the file — free text, because that is exactly what it is.
      { key: 'brokername', label: 'Name In File', columnLabel: 'Name In File', required: true,
        maxLength: 100, width: 220, fullWidth: true },
      { key: 'brokercode', label: 'Broker / Forwarder', type: 'select', optionsEndpoint: '/cms-lookups/brokers',
        showDescriptions: true, showSelectedInfo: true, fkZeroBlank: true, required: true,
        inTable: false, fullWidth: true, refNameKey: 'brname', deletedKey: 'brdeleted' },
      { key: 'brname', label: 'Broker / Forwarder', titleCase: true, width: 220, idKey: 'brokercode',
        deletedKey: 'brdeleted', editable: false },
      { key: 'brcity', label: 'City', titleCase: true, width: 120, editable: false },
      { key: 'brstate', label: 'State', width: 55, align: 'center', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Upload GL Code Mapping (cms.upload_glcodemap) — Integrations → Upload. Maps an inbound
  // location name to a GL code. Config-only, generic drawer: Location + GL Code are open-text
  // full-width inputs. Only Location required (GL Code optional); UNIQUE (location, glcode) →
  // dup-guard + reactivate. Standard `deleted` soft-delete.
  {
    key: 'upload-glcodemap',
    label: 'Upload GL Code Mapping',
    singular: 'GL Code Map',
    group: 'integrations',
    subgroup: 'Upload',
    route: '/data-management/upload-gl-code-mapping',
    searchPlaceholder: 'Search upload GL code mappings…',
    fields: [
      { key: 'location', label: 'Location', required: true, fullWidth: true, maxLength: 100, width: 220 },
      { key: 'glcode', label: 'GL Code', fullWidth: true, maxLength: 50, width: 200 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Vendors — the AP vendor directory (distinct from Repair Vendors). Every identity
  // field is read-only (managed in Sage); the only writes are the Deleted toggle and
  // the contacts subtable, so this runs on the generic drawer: one 'Vendor' field
  // group for the identity block + a ContactsPanel via renderPanels (see
  // drawerPanels.tsx). `createddatetime` / `mas90code_old` are drawer-only context.
  {
    key: 'vendors',
    label: 'Vendors',
    singular: 'Vendor',
    group: 'billing',
    route: '/data-management/vendors',
    searchPlaceholder: 'Search vendors…',
    createLocked: true,
    drawerWidth: '56rem',
    fields: [
      { key: 'name', label: 'Vendor Name', columnLabel: 'Vendor', width: 200, editable: false, group: 'Vendor', fullWidth: true },
      { key: 'mas90code', label: 'Sage Tag', width: 70, editable: false, group: 'Vendor' },
      { key: 'creditterms', label: 'Credit Terms', editable: false, inTable: false, group: 'Vendor' },
      { key: 'address1', label: 'Address 1', width: 180, editable: false, group: 'Vendor' },
      { key: 'address2', label: 'Address 2', width: 130, editable: false, group: 'Vendor' },
      { key: 'city', label: 'City', width: 110, editable: false, group: 'Vendor' },
      { key: 'state', label: 'State', width: 50, align: 'center', editable: false, group: 'Vendor' },
      { key: 'zip', label: 'Zip', width: 60, editable: false, group: 'Vendor' },
      { key: 'country', label: 'Country', width: 70, editable: false, group: 'Vendor' },
      { key: 'phone', label: 'Phone', width: 100, editable: false, group: 'Vendor' },
      { key: 'createddatetime', label: 'Created', editable: false, inTable: false, group: 'Vendor' },
      { key: 'mas90code_old', label: 'MAS Tag (old)', editable: false, inTable: false, group: 'Vendor' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // LC Groups (cms.csrgroups) — generic field-spec drawer, standard `deleted`
  // soft-delete. Name + Display Name are open text; Name required. The legacy
  // "CSR" naming is surfaced as "LC" (Logistics Coordinator); the engine key /
  // DB table stays `csrgroups`.
  {
    key: 'csrgroups',
    label: 'LC Groups',
    singular: 'LC Group',
    group: 'cms',
    route: '/data-management/lc-groups',
    searchPlaceholder: 'Search LC groups…',
    fields: [
      { key: 'name', label: 'Name', required: true, maxLength: 25, width: 150 },
      { key: 'displayname', label: 'Display Name', maxLength: 25, width: 150 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // CMS Codes (cms.cmscodes) — generic field-spec drawer, standard `deleted`
  // soft-delete. cmscode + LC required; cmscode is create-only. The "LC" field
  // (V1 column `csr`) is a select sourced from the shared CSR roster endpoint;
  // it stores the CSR username (grid shows the raw username, like V1).
  {
    key: 'cmscodes',
    label: 'CMS Codes',
    singular: 'CMS Code',
    group: 'cms',
    route: '/data-management/cms-codes',
    searchPlaceholder: 'Search CMS codes…',
    fields: [
      { key: 'cmscode', label: 'CMS Code', required: true, createOnly: true, maxLength: 25, width: 150 },
      { key: 'description', label: 'Description', maxLength: 25, width: 150 },
      { key: 'csr', label: 'LC', required: true, type: 'select', optionsEndpoint: '/cms-lookups/csrs', width: 150 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // DCs (cms.dc_map — customer distribution centers) — CMS Code + DC Number/Name open
  // text + Customer & Dray Location pickers (/cms-lookups/customers + /locations). The
  // grid also shows the backend's computed display names (custname + lname/lcity/lstate),
  // which are editable:false so they stay out of the drawer. Customer, Dray Location and
  // DC Number are required. Standard `deleted` soft-delete. Generic drawer.
  {
    key: 'dc-map',
    label: 'DCs',
    singular: 'DC',
    group: 'cms',
    route: '/data-management/dcs',
    searchPlaceholder: 'Search DCs…',
    fields: [
      { key: 'gwimove', label: 'CMS Code', width: 100 },
      // Display-only join of `customercode`; editable:false keeps it out of the drawer.
      { key: 'custname', label: 'Customer', titleCase: true, width: 160, idKey: 'customercode',
        deletedKey: 'custdeleted', editable: false },
      // The editable FK behind `custname` — drawer only, so the grid keeps its columns.
      { key: 'customercode', label: 'Customer', type: 'select', optionsEndpoint: '/cms-lookups/customers',
        showDescriptions: true, showSelectedInfo: true, fkZeroBlank: true, required: true,
        inTable: false, fullWidth: true, refNameKey: 'custname', deletedKey: 'custdeleted' },
      { key: 'number', label: 'DC Number', width: 100, required: true },
      { key: 'name', label: 'DC Name', width: 200 },
      { key: 'locationcode', label: 'Dray Location', columnLabel: 'Location Code', width: 90, align: 'center',
        type: 'select', optionsEndpoint: '/cms-lookups/locations', showDescriptions: true,
        showSelectedInfo: true, fkZeroBlank: true, required: true, fullWidth: true,
        refNameKey: 'lname', deletedKey: 'locdeleted' },
      { key: 'lname', label: 'Location Name', titleCase: true, width: 200,
        deletedKey: 'locdeleted', editable: false },
      { key: 'lcity', label: 'City', titleCase: true, width: 120, editable: false },
      { key: 'lstate', label: 'State', width: 60, align: 'center', editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Chassis Providers (cms.chassisprovider) — generic field-spec drawer, standard
  // `deleted` soft-delete. Name + Abbreviation + Type required; Type is a fixed
  // POOL/PROVIDER select (non-blank); Abbreviation is create-only (locked on
  // update); chassisrate is numeric. No uniqueness guard.
  {
    key: 'chassisprovider',
    label: 'Chassis Providers',
    singular: 'Chassis Provider',
    group: 'cms',
    route: '/data-management/chassis-providers',
    searchPlaceholder: 'Search chassis providers…',
    fields: [
      { key: 'chassisprovidername', label: 'Name', required: true, maxLength: 40, width: 200 },
      { key: 'abbreviation', label: 'Abbrev', required: true, createOnly: true, maxLength: 12, width: 120 },
      { key: 'providertype', label: 'Type', type: 'select', required: true, options: ['', 'POOL', 'PROVIDER'], width: 120 },
      { key: 'chassisrate', label: 'Rate', type: 'number', maxLength: 12, width: 160 },
      { key: 'recno', label: 'Recno', type: 'number', width: 90, align: 'right', editable: false },
    ],
  },
  // Container Status Reports (cms.contstatusreport) — generic drawer, three field
  // groups: 'Target' (at least one of Deliver To / Bill To / Customer / Broker / CMS
  // Code, enforced by requireOneOf and again by the backend), 'Email' and 'Schedule'.
  // Every FK carries refNameKey + deletedKey so a report aimed at a since-deleted
  // target still shows the name and says so. CMS Code now uses the shared
  // /cms-lookups/cms-codes (cms.cmscodes) rather than V1's DISTINCT ordersall.gwimove
  // — Lee's call 2026-08-25: there was no reason to source it from the orders table.
  // Email From is /cms-lookups/csrs, the ASSIGNMENT roster (bare username), unlike
  // Expiration Report's CSR filter. Standard `deleted` soft-delete.
  {
    key: 'contstatusreport',
    label: 'Container Status Reports',
    singular: 'Container Status Report',
    group: 'cms',
    route: '/data-management/container-status-reports',
    searchPlaceholder: 'Search container status reports…',
    requireOneOf: {
      fields: ['deliverto', 'billto', 'customercode', 'brokercode', 'cmscode'],
      message: 'Enter at least one of Deliver To, Bill To, Customer, Broker, or CMS Code.',
      group: 'Target',
      hint: 'at least one',
    },
    fields: [
      // Grid-only display joins of the four FK targets below.
      { key: 'lname', label: 'Deliver To', titleCase: true, width: 130, idKey: 'deliverto', deletedKey: 'ldeleted', editable: false },
      { key: 'cname', label: 'Bill To', titleCase: true, width: 130, idKey: 'billto', deletedKey: 'bdeleted', editable: false },
      { key: 'custname', label: 'Customer', titleCase: true, width: 130, idKey: 'customercode', deletedKey: 'cdeleted', editable: false },
      { key: 'name', label: 'Broker', titleCase: true, width: 130, idKey: 'brokercode', deletedKey: 'brdeleted', editable: false },
      // Target — none individually required, but requireOneOf covers the set.
      { key: 'deliverto', label: 'Deliver To', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/locations', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, inTable: false, refNameKey: 'lname', deletedKey: 'ldeleted' },
      { key: 'billto', label: 'Bill To', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/billtos', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, inTable: false, refNameKey: 'cname', deletedKey: 'bdeleted' },
      { key: 'customercode', label: 'Customer', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/customers', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, inTable: false, refNameKey: 'custname', deletedKey: 'cdeleted' },
      { key: 'brokercode', label: 'Broker', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/brokers', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, inTable: false, refNameKey: 'name', deletedKey: 'brdeleted' },
      { key: 'cmscode', label: 'CMS Code', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/cms-codes', width: 80 },
      { key: 'movetype', label: 'Movetype', group: 'Target', type: 'select',
        options: [...CSR_MOVETYPE_OPTS], width: 80 },
      // Email.
      { key: 'subjectoverride', label: 'Subject Override', columnLabel: 'Subject',
        group: 'Email', maxLength: 50, width: 130, fullWidth: true },
      { key: 'emailto', label: 'Email To', group: 'Email', type: 'textarea',
        required: true, email: true, fullWidth: true, width: 160 },
      { key: 'emailfrom', label: 'Email From', group: 'Email', type: 'select',
        optionsEndpoint: '/cms-lookups/csrs', width: 90 },
      // Schedule.
      { key: 'frequency', label: 'Frequency', group: 'Schedule', type: 'select', required: true,
        optionsEndpoint: '/cms-lookups/schedule-frequencies', width: 90 },
      { key: 'time', label: 'Time', group: 'Schedule', type: 'select', required: true,
        optionsEndpoint: '/cms-lookups/schedule-times', width: 60 },
      { key: 'lastsentdatetime', label: 'Last Sent', width: 120, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Expiration Reports (cms.expreport) — generic drawer, three field groups: 'Target'
  // (all optional — CSR / Bill To / Customer / Warehouse), 'Email' (the required
  // comma-delimited list) and 'Schedule' (Frequency + Time required, Days until
  // Expiration optional, Last Sent read-only). CSR uses /cms-lookups/report-csrs, the
  // FILTER roster whose values are the compound 'csr~~<username>' — NOT /cms-lookups/csrs,
  // which is the assignment roster keyed on the bare username. Standard soft-delete.
  {
    key: 'expreport',
    label: 'Expiration Report',
    singular: 'Expiration Report',
    group: 'cms',
    route: '/data-management/expiration-report',
    searchPlaceholder: 'Search expiration reports…',
    fields: [
      // Grid-only display joins of the FK targets below.
      { key: 'csrname', label: 'CSR', titleCase: true, width: 130, editable: false },
      { key: 'cname', label: 'Bill To', titleCase: true, width: 170, idKey: 'billto', deletedKey: 'billtodeleted', editable: false },
      { key: 'custname', label: 'Customer', titleCase: true, width: 170, idKey: 'customercode', deletedKey: 'custdeleted', editable: false },
      // Target — every field optional (V1 editexpreport).
      { key: 'csr', label: 'CSR', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/report-csrs', inTable: false },
      { key: 'billto', label: 'Bill To', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/billtos', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, inTable: false, refNameKey: 'cname', deletedKey: 'billtodeleted' },
      { key: 'customercode', label: 'Customer', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/customers', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, inTable: false, refNameKey: 'custname', deletedKey: 'custdeleted' },
      { key: 'whse', label: 'Warehouse', group: 'Target', type: 'select',
        optionsEndpoint: '/cms-lookups/warehouses?include_all=true', width: 80 },
      // Email — required, validated as a comma/semicolon-delimited list.
      // Label stays short: it is interpolated into "<label> is required." and
      // "<label> must be valid email address(es)…" in the drawer's error banner.
      { key: 'emailto', label: 'Email To', group: 'Email', type: 'textarea',
        required: true, email: true, fullWidth: true, width: 160 },
      // Schedule.
      { key: 'frequency', label: 'Frequency', group: 'Schedule', type: 'select', required: true,
        optionsEndpoint: '/cms-lookups/schedule-frequencies', width: 90 },
      { key: 'time', label: 'Time', group: 'Schedule', type: 'select', required: true,
        optionsEndpoint: '/cms-lookups/schedule-times', width: 60 },
      { key: 'daysexp', label: 'Days until Expiration', group: 'Schedule', type: 'number', width: 90 },
      { key: 'lastsentdatetime', label: 'Last Sent', width: 120, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'fsc-tariff',
    label: 'Fuel Surcharge Tariff',
    singular: 'Fuel Surcharge Tariff',
    group: 'rates', // decluttered out of CMS → Rates, Fuel & Per Diem
    route: '/data-management/fuel-surcharge-tariff',
    searchPlaceholder: 'Search tariffs…',
    // Table-view columns (the create/edit experience is FscTariffEditDrawer, which
    // also manages the fsc_tarifflines sub-editor + the New Revision action).
    // `billto_all`/`sagetag_all` are engine computed columns: the primary Bill To
    // (from cms) plus the tariff's additional bill-tos (fsc_tariff_billto junction),
    // newline-joined and stacked in the cell (see `stackedLines`); `name` is a
    // free-text base column (replaced the old per-customer link).
    fields: [
      { key: 'tariffnumber', label: 'Tariff #', width: 70, editable: false },
      { key: 'revisionnumber', label: 'Revision', width: 70, editable: false },
      { key: 'tarifftype', label: 'Type', width: 120, editable: false },
      { key: 'billto_all', label: 'Bill To', width: 200, titleCase: true, stackedLines: true, editable: false },
      { key: 'sagetag_all', label: 'Sage Tag', width: 90, stackedLines: true, editable: false },
      { key: 'name', label: 'Name/Description', width: 180, editable: false },
      { key: 'effectivedate', label: 'Eff. Date', type: 'date', width: 90, editable: false },
      { key: 'expirationdate', label: 'Exp. Date', type: 'date', width: 90, editable: false },
      { key: 'otrlocal', label: 'Local/OTR', width: 90, editable: false },
      { key: 'ratetype', label: 'Rate Type', width: 90, editable: false },
      { key: 'fuelindex', label: 'Fuel Index', width: 90, editable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // Fuel Surcharge - Customer (fueldata.fsc_customer) — a flat per-fuel-index customer
  // surcharge band table (no sub-editor, unlike Fuel Surcharge Tariff). Generic drawer:
  // fuelindex + Customer FSC are decimals; effectivedate uses the
  // numeric-shortcut date picker. fuelindex + effectivedate are required and form the
  // table's UNIQUE KEY (dup-checked with a clean error). No soft-delete column.
  {
    key: 'fsc-customer',
    latestDateFilter: { column: 'effectivedate', label: 'Effective Date' },
    enableColumnEditing: false,
    enableTotalsBar: false,
    notice: 'Fuel Index is based on the National Index.',
    label: 'Fuel Surcharge - Customer',
    singular: 'Customer Fuel Surcharge',
    group: 'rates',
    route: '/data-management/fuel-surcharge-customer',
    noSoftDelete: true, // fueldata.fsc_customer has no `deleted` column (backend soft_delete=None)
    searchPlaceholder: 'Search customer fuel surcharges…',
    fields: [
      { key: 'fuelindex', label: 'Fuel Index', type: 'number', required: true, width: 90 },
      { key: 'drayotr', label: 'Customer FSC', type: 'number', width: 110 },
      { key: 'effectivedate', label: 'Effective Date', type: 'date', required: true, width: 120 },
      { key: 'recno', label: 'Record', type: 'number', width: 80, align: 'right', editable: false, inTable: false },
    ],
  },
  // Fuel Surcharge - Driver (fueldata.fsc_driver) — sibling of Fuel Surcharge - Customer:
  // a flat per-fuel-index driver surcharge band table. Generic drawer: fuelindex + the
  // three local/OTR/SE-OTR rates are decimals, effectivedate uses the numeric-shortcut
  // date picker. fuelindex + effectivedate are required and form the UNIQUE KEY
  // (dup-checked with a clean error). No soft-delete column.
  {
    key: 'fsc-driver',
    label: 'Fuel Surcharge - Driver',
    singular: 'Driver Fuel Surcharge',
    group: 'rates',
    route: '/data-management/fuel-surcharge-driver',
    noSoftDelete: true, // fueldata.fsc_driver has no `deleted` column (backend soft_delete=None)
    searchPlaceholder: 'Search driver fuel surcharges…',
    fields: [
      { key: 'fuelindex', label: 'Fuel Index', type: 'number', required: true, width: 90 },
      { key: 'fuellocal', label: 'Local', type: 'number', width: 110 },
      { key: 'fuelotr', label: 'OTR', type: 'number', width: 110 },
      { key: 'fuelsoutheastotr', label: 'SE OTR', type: 'number', width: 110 },
      { key: 'effectivedate', label: 'Effective Date', type: 'date', required: true, width: 120 },
      { key: 'recno', label: 'Record', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // Current Fuel Index — read-only view of the weekly fueldata.fuelindex readings with the
  // customer FSC and driver rates the two tables above map them to (V1 fuelcharge.pl port,
  // two tabs on one page). Bespoke page, not an engine table.
  { key: 'fuel-index', label: 'Current Fuel Index', group: 'rates', route: '/data-management/fuel-index', custom: true },

  // Fuel Pump Pricing — Pilot truck-stop prices (daily "Better Of" sheet import)
  // + per-site driver-region tags that gate what gwiMove drivers see.
  { key: 'fuel-pump-pricing', label: 'Fuel Pump Pricing', group: 'rates', route: '/data-management/fuel-pump-pricing', custom: true },
  // Customer Per Diem Rules (cms.customerperdiem) — Rates, Fuel & Per Diem. The generic
  // replacement for V1's per-customer per-diem hardcodes (saverules sr_perdiemstart free-days
  // blocks + libfast_cms checkpdamounts tiered-rate blocks). One row = one request-form line:
  // identity scope (Bill To and/or Customer recno) × match columns
  // (SCAC/Move Type/Mode with ALL, size + equip-type multiselects blank = any) × optional
  // free-days section × optional tiered-amounts section × required expiration date.
  // Bespoke CustomerPerdiemEditDrawer, request-form-style grid both ways: create picks
  // the Bill To/Customer once and enters N SCAC rows; clicking a rule opens the WHOLE
  // scope's rules for group editing in SCAC order (grid list_order groups by scope).
  // ONE row per (billto, customercode, scac, movetype, sizes, equiptypes) — the
  // dup-guard allows movetype/size/equip splits on one SCAC; renewals EDIT the row in
  // place. `active` = computed "not yet expired" flag; `deleted` is the soft-delete.
  // Resolution at apply time: customerperdiem → billtos → sslperdiem.
  {
    key: 'customer-perdiem',
    label: 'Customer Per Diem Rules',
    singular: 'Per Diem Rule',
    group: 'rates',
    route: '/data-management/customer-per-diem',
    searchPlaceholder: 'Search per diem rules…',
    fields: [
      { key: 'active', label: 'Active', type: 'yn', align: 'center', width: 50, editable: false },
      { key: 'billtoname', label: 'Bill To', width: 150, titleCase: true, idKey: 'billto', deletedKey: 'billtodeleted', editable: false },
      { key: 'customername', label: 'Customer', width: 150, titleCase: true, idKey: 'customercode', deletedKey: 'custdeleted', editable: false },
      { key: 'brokername', label: 'Broker', width: 150, titleCase: true, idKey: 'brokercode', deletedKey: 'brokerdeleted', editable: false },
      { key: 'scac', label: 'SCAC', align: 'center', width: 60, deletedKey: 'scacdeleted', editable: false },
      { key: 'movetype', label: 'Move Type', align: 'center', width: 90, editable: false },
      { key: 'mode', label: 'Mode', align: 'center', width: 60, editable: false },
      { key: 'sizelist', label: 'Sizes', align: 'center', width: 80, editable: false },
      { key: 'equiptypelist', label: 'Equip Types', width: 130, editable: false },
      { key: 'freedays', label: 'Days', align: 'center', width: 50, editable: false },
      { key: 'freedaystype', label: 'Days Type', align: 'center', width: 90, editable: false },
      { key: 't1amount', label: 'Amt/Day', align: 'right', width: 80, editable: false },
      { key: 't2days', label: 'T2 Start', align: 'center', width: 70, editable: false },
      { key: 't2amount', label: 'T2 Amt', align: 'right', width: 75, editable: false },
      { key: 'expirationdate', label: 'Exp. Date', type: 'date', width: 90, editable: false },
      { key: 'contractnumber', label: 'Contract #', width: 100, editable: false },
      // Live count of orderscurrent rows the rule's match columns apply to —
      // "does anyone still ship under this rule?" at a glance (computed).
      { key: 'matchingorders', label: 'Open Orders', type: 'number', align: 'right', width: 90, editable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // CMS standalone-page conversions (Phase 5 — converted onto the engine one at a time).
  {
    key: 'contact-types',
    label: 'Contact Types',
    singular: 'Contact Type',
    group: 'cms',
    route: '/data-management/contact-types',
    searchPlaceholder: 'Search contact types…',
    // Small soft-delete lookup; rows feed the contacts modal's Type dropdown
    // (served separately via /contacts/types) on Customers, Locations & Bill Tos.
    fields: [
      { key: 'type', label: 'Type', required: true, titleCase: true, width: 320, placeholder: 'e.g., DISPATCH, ACCOUNTING' },
      { key: 'priority', label: 'Priority', type: 'number', width: 120, defaultValue: '1' },
      { key: 'recno', label: 'Record', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // Billing & Additional Costs — flat cms lookup feeding the Additional Cost
  // item drawer's Dispute Reason dropdown. The engine appends the Deleted
  // column; no recno shown (matches legacy). reason is a required open-text field.
  {
    key: 'additionalcost-dispute-reasons',
    label: 'Additional Cost Dispute Reasons',
    singular: 'Additional Cost Dispute Reason',
    group: 'billing',
    route: '/data-management/additional-cost-dispute-reasons',
    searchPlaceholder: 'Search dispute reasons…',
    fields: [
      { key: 'reason', label: 'Reason', required: true, width: 250, fullWidth: true },
    ],
  },
  // Sibling of dispute-reasons; feeds the Additional Cost item drawer's Hold
  // Type dropdown. Engine appends the Deleted column; type is a required field.
  {
    key: 'additionalcost-hold-types',
    label: 'Additional Cost Hold Types',
    singular: 'Additional Cost Hold Type',
    group: 'billing',
    route: '/data-management/additional-cost-hold-types',
    searchPlaceholder: 'Search hold types…',
    fields: [
      { key: 'type', label: 'Type', required: true, width: 250, fullWidth: true },
    ],
  },
  // Sibling of dispute-reasons/hold-types; feeds the Additional Cost item
  // drawer's Reason dropdown. Engine appends the Deleted column; reason required.
  {
    key: 'additionalcost-reasons',
    label: 'Additional Cost Reasons',
    singular: 'Additional Cost Reason',
    group: 'billing',
    route: '/data-management/additional-cost-reasons',
    searchPlaceholder: 'Search reasons…',
    fields: [
      { key: 'reason', label: 'Reason', required: true, width: 250, fullWidth: true },
    ],
  },
  // GL Codes (cms.glcodes) — an Invoicing lookup mapping a GL code to a sales code +
  // description. Config-only, generic drawer + standard `deleted` soft-delete. Only
  // GL Code is required; legacy allowed duplicate GL codes → no dup-guard. creditgl is
  // a Y/N flag, salescode a whole number.
  {
    key: 'glcodes',
    label: 'GL Codes',
    singular: 'GL Code',
    group: 'billing',
    route: '/data-management/gl-codes',
    searchPlaceholder: 'Search GL codes…',
    fields: [
      { key: 'glcode', label: 'GL Code', required: true, maxLength: 20, width: 100 },
      { key: 'salescode', label: 'Sales Code', type: 'number', integer: true, width: 100 },
      { key: 'description', label: 'Description', maxLength: 60, width: 200 },
      { key: 'creditgl', label: 'Credit GL', type: 'yn', width: 70, align: 'center' },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Invoice Line Items (cms.invoice_litypes) — line-item types with a display name +
  // GL type/code. Config-only, generic drawer + standard `deleted` soft-delete. Name is
  // required and the UNIQUE key: createOnly (locked after the initial add) + dup-guarded.
  // gltype is a fixed REGION/SINGLE/WAREHOUSE select (no blank, defaults to REGION).
  {
    key: 'invoice-litypes',
    label: 'Invoice Line Items',
    singular: 'Invoice Line Item',
    group: 'billing',
    route: '/data-management/invoice-line-items',
    searchPlaceholder: 'Search invoice line items…',
    fields: [
      { key: 'name', label: 'Name', required: true, createOnly: true, fullWidth: true, maxLength: 60, width: 180 },
      { key: 'displayname', label: 'Display Name', fullWidth: true, maxLength: 60, width: 180 },
      { key: 'gltype', label: 'GL Type', type: 'select', clearable: false, defaultValue: 'REGION', width: 130, options: ['REGION', 'SINGLE', 'WAREHOUSE'] },
      { key: 'glcode', label: 'GL Code', maxLength: 20, width: 100 },
      // Quote-charge mapping moved to the Quote Charge Mapping page (many charges →
      // one line item). The grid still shows every mapped charge (computed
      // GROUP_CONCAT, legacy single-slot as fallback), read-only here.
      { key: 'additionalcharge_desc', label: 'Quote Charges', editable: false, width: 220 },
      // Migration 522. Does the invoice reconciler WRITE this line item to real invoices?
      // Never changes what is derived or shown — the Invoice Estimate, the /pro Invoicing
      // callouts and the est_* columns are unaffected — only whether apply_plan applies it.
      // ANDed with the RECONCILER_APPLY env var, and only the strict derivations (Chassis
      // Management, Fuel Surcharge) can be written at all, so ticking it on an
      // estimate-only charge has no effect. Defaults N; it exists so that enabling — and
      // reverting — one derivation is a checkbox rather than a deploy.
      { key: 'reconciler_apply', label: 'Reconciler Writes', type: 'yn', width: 150, align: 'center' },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Quote Charge Mapping (cms.invoice_litype_addlcharges) — which invoice line item a
  // quote charge bills under, MANY charges → one line item (replaces the single-slot
  // select that lived on Invoice Line Items). Read by Pro Invoicing "Add to Invoice"
  // and the Invoice Estimate. The grid lists EVERY active quote charge (list_sql) with
  // a color-coded verdict (MAPPED / NAME MATCH / NOT MAPPED); a charge maps at most
  // once — dup-guarded server-side on Quote Charge. noSoftDelete hides the engine's
  // Active/Deleted chrome — un-mapping is the drawer's "Remove Mapping" button
  // (QuoteChargeMappingEditDrawer), and a soft-deleted row simply reads NOT MAPPED.
  // Requires backend migration 454.
  {
    key: 'invoice-litype-charge-map',
    label: 'Quote Charge Mapping',
    singular: 'Quote Charge Mapping',
    group: 'billing',
    route: '/data-management/quote-charge-mapping',
    searchPlaceholder: 'Search quote charge mappings…',
    noSoftDelete: true,
    fields: [
      { key: 'additionalcharge_recno', label: 'Quote Charge', type: 'select', optionsEndpoint: '/cms-lookups/additionalcharges', showDescriptions: true, required: true, clearable: false, defaultValue: '0', inTable: false, fullWidth: true },
      { key: 'additionalcharge_desc', label: 'Quote Charge', editable: false, width: 240 },
      // Region + Rate Type tell catalog near-duplicates apart (two CHASSIS SPLITs,
      // one of them MEMPHIS) — same detail the drawer's Quote Charge select shows.
      { key: 'region', label: 'Region', editable: false, titleCase: true, width: 110 },
      { key: 'ratetype', label: 'Rate Type', editable: false, titleCase: true, width: 100 },
      // fkZeroBlank: the litypes endpoint has no '— None —' sentinel row (it also
      // feeds the Pro Invoicing Item typeahead), so an unset FK must render as an
      // empty box, not a literal orange '0'. Blank writes back as '0', which the
      // server rejects with a clean "Invoice Line Item is required".
      { key: 'litype_recno', label: 'Invoice Line Item', type: 'select', optionsEndpoint: '/cms-lookups/invoice-litypes', showDescriptions: true, required: true, clearable: false, fkZeroBlank: true, inTable: false, fullWidth: true },
      { key: 'litype_name', label: 'Invoice Line Item', editable: false, width: 200 },
      { key: 'mapstatus', label: 'Status', editable: false, width: 130, badgeColors: { 'MAPPED': 'green', 'NAME MATCH': 'amber', 'NOT MAPPED': 'red' } },
      // No Recno column: most rows are unmapped quote charges with no mapping record
      // (recno 0), so the number is noise — the drawer owns record identity.
    ],
  },
  // Invoice Hold Reasons (cms.invoice_holdreasons) — the user-maintainable reason list for
  // the Pro Invoicing "Hold Reason" dropdown (replaces the hardcoded INVOICE_HOLD_REASONS).
  // Config-only, generic drawer + standard `deleted` soft-delete. Reason is required and the
  // UNIQUE key (dup-guarded + reactivate-on-dup); max 50 chars.
  {
    key: 'invoice-holdreasons',
    label: 'Invoice Hold Reasons',
    singular: 'Invoice Hold Reason',
    group: 'billing',
    route: '/data-management/invoice-hold-reasons',
    searchPlaceholder: 'Search invoice hold reasons…',
    fields: [
      { key: 'reason', label: 'Reason', required: true, maxLength: 50, fullWidth: true, width: 300 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Override Reasons (cms.unbilled_reasons; renamed from "Unbilled Reasons" in the UI only,
  // the table and route keep the old name) — the user-maintainable LC-reason list for the
  // Unbilled Charges review page (Researching/Reports). Config-only, generic drawer +
  // standard `deleted` soft-delete. Reason is required and the UNIQUE key (dup-guarded +
  // reactivate-on-dup); max 50 chars. Stored in unbilled_charge_reviews.lc_reason and
  // invoice_lineitem_override.reason. Require Comments (migration 495): the Line Item
  // Override modal on the Pro Invoicing tab refuses to save that reason without
  // Additional Comments. Category is an optional reporting rollup above Reason.
  {
    key: 'unbilled-reasons',
    label: 'Override Reasons',
    singular: 'Override Reason',
    group: 'billing',
    route: '/data-management/unbilled-reasons',
    searchPlaceholder: 'Search override reasons…',
    fields: [
      { key: 'reason', label: 'Reason', required: true, maxLength: 50, fullWidth: true, width: 300 },
      { key: 'reason_category', label: 'Category', type: 'select', optionsFromRows: true,
        allowNewOptions: true, maxLength: 100, width: 180 },
      { key: 'require_comments', label: 'Require Comments', type: 'yn', width: 130, align: 'center' },
      { key: 'responsible_department', label: 'Responsible Department', type: 'select', width: 220,
        options: ['CLAIMS', 'CST/OUTSIDE CARRIER', 'DRIVER RETENTION', 'EXECUTIVES', 'IT',
          'MAINTENANCE', 'SALES', 'T&D/RECON', 'TRANS CUSTOMER SERVICE', 'TRANS OPS',
          'WHSE CUSTOMER SERVICE', 'WHSE OPS'] },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Paperwork Rejection Reasons (cms.driverpaperwork_rejectionreason) — the user-maintainable
  // reason list for the leg-document Reject Paperwork dropdown (V1 Randy Collier DS2-1845).
  // Config-only, generic drawer + standard `deleted` soft-delete. Reason is the only field
  // and is required (dup-guarded + reactivate-on-dup). Stored in legdocumentinfo.rejectedreason.
  {
    key: 'paperwork-rejection-reasons',
    label: 'Paperwork Rejection Reasons',
    singular: 'Paperwork Rejection Reason',
    group: 'driverpay',
    route: '/data-management/paperwork-rejection-reasons',
    searchPlaceholder: 'Search paperwork rejection reasons…',
    fields: [
      { key: 'reason', label: 'Reason', required: true, fullWidth: true, width: 300 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Hazmat Data (cms.hazmatdata) — reference catalogue of hazmat entries (UN / shipping
  // name / class / package). ADD-ONLY: every field is createOnly (editable when adding,
  // locked when viewing an existing row) — see backend update_locked. No soft-delete
  // column (noSoftDelete). No required fields, but the four fields form a combined UNIQUE
  // KEY, so a duplicate add is rejected with a clean error (backend keys dup-guard).
  {
    key: 'hazmatdata',
    label: 'Hazmat Data',
    singular: 'Hazmat Entry',
    group: 'cms',
    route: '/data-management/hazmat-data',
    noSoftDelete: true,
    searchPlaceholder: 'Search hazmat data…',
    fields: [
      { key: 'hazmatun', label: 'Hazmat UN', columnLabel: 'UN', createOnly: true, maxLength: 20, width: 80 },
      { key: 'hazmatshippingname', label: 'Shipping Name', type: 'textarea', createOnly: true, fullWidth: true, width: 500 },
      { key: 'hazmatclass', label: 'Class', createOnly: true, maxLength: 20, width: 70 },
      { key: 'hazmatpkg', label: 'Package', columnLabel: 'Pkg', createOnly: true, maxLength: 20, width: 70 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Markets (cms.markets) — a market name + its sales rep (quotes-adjacent lookup).
  // Config-only, generic drawer + standard `deleted` soft-delete. Market is required
  // open text; Sales Rep is a select of active sales reps (username value / full-name
  // label) from /cms-lookups/salesreps. No dup-guard (legacy allowed duplicate names).
  {
    key: 'markets',
    label: 'Markets',
    singular: 'Market',
    group: 'cms',
    route: '/data-management/markets',
    searchPlaceholder: 'Search markets…',
    fields: [
      { key: 'market', label: 'Market', required: true, maxLength: 100, width: 250 },
      { key: 'salesrep', label: 'Sales Rep', type: 'select', optionsEndpoint: '/cms-lookups/salesreps', width: 200 },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Outbound Modes (cms.outboundmode) — a simple lookup of outbound mode names.
  // Config-only, generic drawer + standard `deleted` soft-delete. Mode is required
  // open text; no dup-guard (legacy allowed duplicate mode names). Warehouse group.
  {
    key: 'outboundmode',
    label: 'Outbound Modes',
    singular: 'Outbound Mode',
    group: 'warehouse',
    route: '/data-management/outbound-modes',
    searchPlaceholder: 'Search outbound modes…',
    fields: [
      { key: 'mode', label: 'Mode', required: true, maxLength: 30, width: 250 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Overweight Exceptions (cms.overweight_exceptions) — per customer/lane/equipment
  // weight-limit exceptions. Generic drawer, two field groups: 'Lane' (Customer +
  // Origin/Destination pickers off /cms-lookups/customers + /locations; the location
  // labels are 'ALIAS - Name', so typing either narrows the picker, as on the order
  // form — see cms_lookups._location_label) and 'Equipment'
  // (Size/Type/Chassis Type from the order-form constants, Weight Limit, Daycab). The
  // grid keeps the joined names (computed); their editable FK partners are inTable:false.
  // Each picker carries refNameKey + deletedKey so a rule pointing at a since-deleted
  // customer/location still shows the name and says so. All required except Chassis Type
  // and Daycab; seven fields form a UNIQUE key (dup-guard + reactivate).
  {
    key: 'overweight-exceptions',
    label: 'Overweight Exceptions',
    singular: 'Overweight Exception',
    group: 'cms',
    route: '/data-management/overweight-exceptions',
    searchPlaceholder: 'Search overweight exceptions…',
    fields: [
      // The joined display names are grid-only (editable:false keeps them out of the
      // drawer, where their editable FK partners stand in for them instead).
      { key: 'custname', label: 'Customer', titleCase: true, width: 200, deletedKey: 'custdeleted', editable: false },
      // Registry order serves both surfaces: this sits 2nd so the grid keeps its
      // 'Cust Code' column where it was, and it is first in the Lane group.
      { key: 'customercode', label: 'Customer', columnLabel: 'Cust Code', group: 'Lane', type: 'select',
        optionsEndpoint: '/cms-lookups/customers', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, required: true, fullWidth: true, align: 'center', width: 80,
        refNameKey: 'custname', deletedKey: 'custdeleted' },
      { key: 'origname', label: 'Origin', titleCase: true, width: 150, deletedKey: 'origdeleted', editable: false },
      { key: 'destname', label: 'Destination', titleCase: true, width: 150, deletedKey: 'destdeleted', editable: false },
      { key: 'origcode', label: 'Origin', group: 'Lane', type: 'select',
        optionsEndpoint: '/cms-lookups/locations', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, required: true, inTable: false,
        refNameKey: 'origname', deletedKey: 'origdeleted' },
      { key: 'destcode', label: 'Destination', group: 'Lane', type: 'select',
        optionsEndpoint: '/cms-lookups/locations', showDescriptions: true, showSelectedInfo: true,
        fkZeroBlank: true, required: true, inTable: false,
        refNameKey: 'destname', deletedKey: 'destdeleted' },
      // Drawer — Equipment (also grid columns).
      { key: 'equipsize', label: 'Equip Size', group: 'Equipment', type: 'select', required: true,
        options: [...EQUIPMENT_SIZES], align: 'center', width: 90 },
      { key: 'equiptype', label: 'Equip Type', group: 'Equipment', type: 'select', required: true,
        options: [...EQUIPMENT_TYPES], align: 'center', width: 110 },
      { key: 'chassistype', label: 'Chassis Type', group: 'Equipment', type: 'select',
        options: [...CHASSIS_TYPES], align: 'center', width: 110 },
      { key: 'weightlimit', label: 'Weight Limit', group: 'Equipment', type: 'number', decimal: true,
        required: true, placeholder: 'lbs', align: 'right', width: 100 },
      { key: 'daycab', label: 'Daycab', group: 'Equipment', type: 'yn', align: 'center', width: 70 },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Ports (cms.ports) — the port catalogue (name, country, continent, seaport/firms
  // codes, UN/LOCODE). Bespoke PortsEditDrawer: Port/Country/Continent/Seaport/Firms are
  // open text (Port/Country/Continent required); UN/LOCODE is a readonly code with an
  // inline lookup into cms.un_locations (seeded by the Port value, stores country+location
  // e.g. USSAV); Created is stamped on insert (readonly). No unique key.
  {
    key: 'ports',
    label: 'Ports',
    singular: 'Port',
    group: 'cms',
    route: '/data-management/ports',
    searchPlaceholder: 'Search ports…',
    fields: [
      { key: 'pport', label: 'Port', width: 120, editable: false },
      { key: 'pcountry', label: 'Country', width: 210, editable: false },
      { key: 'pcontinent', label: 'Continent', width: 120, editable: false },
      { key: 'seaportcode', label: 'Seaport Code', width: 120, editable: false },
      { key: 'firmscode', label: 'Firms Code', width: 120, editable: false },
      { key: 'unlocode', label: 'UN/LOCODE', width: 100, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Terminals (cms.rails) — rail/port/other trace terminals. Config-only, generic drawer +
  // standard `deleted` soft-delete. Terminal / Name / Website are open text (all full-width);
  // Type is a RAIL/PORT/OTHER select (blank default); Trace? / Down? are Y/N (default N);
  // Additional Info is a full-width textarea. Terminal + Type required; terminal is UNIQUE
  // (dup-guard + reactivate). Last Attempted / Last Successful are readonly trace-managed
  // datetimes (shown in the drawer's read-only Details block via showReadOnlyContext).
  // Website / Additional Info are drawer-only (inTable:false).
  {
    key: 'rails',
    label: 'Terminals',
    singular: 'Terminal',
    group: 'cms',
    route: '/data-management/terminals',
    searchPlaceholder: 'Search terminals…',
    showReadOnlyContext: true,
    fields: [
      { key: 'terminal', label: 'Terminal', required: true, fullWidth: true, maxLength: 15, width: 150 },
      { key: 'name', label: 'Name', fullWidth: true, maxLength: 50, width: 175 },
      { key: 'website', label: 'Website', fullWidth: true, maxLength: 50, inTable: false },
      { key: 'terminaltype', label: 'Type', type: 'select', required: true, defaultValue: '', options: ['RAIL', 'PORT', 'OTHER'], width: 150 },
      { key: 'dotrace', label: 'Trace?', type: 'yn', align: 'center', width: 60 },
      { key: 'isdown', label: 'Down?', type: 'yn', align: 'center', width: 60 },
      { key: 'additionalinfo', label: 'Additional Info', type: 'textarea', fullWidth: true, inTable: false },
      { key: 'lastattempteddatetime', label: 'Last Attempted', width: 110, editable: false },
      { key: 'lastsuccessfuldatetime', label: 'Last Successful', width: 110, editable: false },
    ],
  },
  // Rate Zones (cms.ratezones) — quote rate-zone catalogue. Config-only, generic drawer +
  // standard `deleted` soft-delete. Rate Zone / Display Name / Quote PDF Description are
  // open text (all full-width); Region is an optional select from /cms-lookups/regions
  // (REGION_OPTIONS). Rate Zone / Display Name / Quote PDF Description required. No unique key.
  {
    key: 'ratezones',
    label: 'Rate Zones',
    singular: 'Rate Zone',
    group: 'cms',
    route: '/data-management/rate-zones',
    searchPlaceholder: 'Search rate zones…',
    fields: [
      { key: 'ratezone', label: 'Rate Zone', required: true, fullWidth: true, maxLength: 30, width: 150 },
      { key: 'displayname', label: 'Display Name', required: true, fullWidth: true, maxLength: 50, width: 150 },
      { key: 'quotepdfdescription', label: 'Quote PDF Description', required: true, fullWidth: true, maxLength: 80, width: 150 },
      { key: 'region', label: 'Region', type: 'select', optionsEndpoint: '/cms-lookups/regions', width: 80 },
      // Delivery-mileage origin ZIP (migration 307): zipcode = LOCAL (+ the "show on map"
      // pin); otr_zipcode = OTR, which may be a two-leg 'ZIP1_ZIP2' round-trip. Optional.
      { key: 'zipcode', label: 'Zip Code', maxLength: 10, width: 90, mapZip: true, placeholder: 'e.g. 77571' },
      { key: 'otr_zipcode', label: 'OTR Zip', maxLength: 20, width: 110, placeholder: "single ZIP or 'ZIP1_ZIP2'" },
      { key: 'recno', label: 'Recno', type: 'number', width: 60, align: 'right', editable: false },
    ],
  },
  // Report Emails (cms.reportemail) — the recipient list per report type. Config-only,
  // generic drawer + standard `deleted` soft-delete. NO adds (createLocked); Report Type is
  // locked (read-only, shown in the drawer's Details block via showReadOnlyContext). Only
  // Email To is editable — a full-width textarea holding a comma-delimited email list
  // (email-format validated). No unique key.
  {
    key: 'reportemail',
    label: 'Report Emails',
    singular: 'Report Email',
    group: 'cms',
    route: '/data-management/report-emails',
    searchPlaceholder: 'Search report emails…',
    createLocked: true,
    showReadOnlyContext: true,
    fields: [
      { key: 'reporttype', label: 'Report Type', columnLabel: 'ReportType', width: 250, editable: false },
      { key: 'emailto', label: 'Email To', columnLabel: 'EmailTo', type: 'textarea', fullWidth: true, email: true, width: 250 },
    ],
  },
  // Service Failure Reasons (cms.reschedulereason) — the reschedule/service-failure reason
  // catalogue. Config-only, generic drawer + standard `deleted` soft-delete. Responsible /
  // ATD Category / Assigned To are fixed selects; Reason is full-width open text; Issue /
  // Description are full-width textareas; Counter? is Y/N (default Y). Reason / Responsible /
  // Assigned To required; reason is UNIQUE (dup-guard + reactivate).
  {
    key: 'reschedulereason',
    label: 'Service Failure Reasons',
    singular: 'Service Failure Reason',
    group: 'cms',
    route: '/data-management/service-failure-reasons',
    searchPlaceholder: 'Search service failure reasons…',
    fields: [
      { key: 'responsible', label: 'Responsible', type: 'select', required: true, width: 80,
        options: ['GWI', 'CUSTOMER', 'LOADING WHSE', 'OTHER CARRIER', 'SSLINE'] },
      { key: 'reason', label: 'Reason', required: true, fullWidth: true, maxLength: 50, width: 150 },
      { key: 'atdcategory', label: 'ATD Category', type: 'select', width: 150, options: [
        'NO POWER', 'CHASSIS NOT AVAILABLE', 'DRIVER RELATED', 'EQUIPMENT RELATED',
        'TERMINAL RELATED', 'WEATHER RELATED', 'RAIL DELAY', 'VESSEL DELAY',
        'NO APPOINTMENT', 'NO PICKUP NUMBER', 'EARLY DELIVERY', 'CONTAINER DAMAGED',
        'SSL HOLD', 'CUSTOM HOLD', 'CONTAINER NOT ARRIVED', 'OTHER',
      ] },
      { key: 'assignedto', label: 'Assigned To', type: 'select', required: true, width: 80,
        options: ['CSR', 'WHSE CSR', 'DISPATCHER'] },
      { key: 'issue', label: 'Issue', type: 'textarea', fullWidth: true, width: 150 },
      { key: 'description', label: 'Description', type: 'textarea', fullWidth: true, width: 150 },
      { key: 'counter', label: 'Counter?', type: 'yn', defaultValue: 'Y', align: 'center', width: 50 },
    ],
  },
  // Tracing Terminal Mapping (cms.tracing_terminalmap) — maps a Gnosis trace terminal string to
  // a CMS rail/port terminal. In the CMS group (tracing-adjacent). Config-only, generic drawer:
  // Tracing Terminal is a full-width open text box (required; UNIQUE → dup-guard + reactivate);
  // Terminal is a select from /cms-lookups/rails (distinct cms.rails terminal names, required).
  // Standard `deleted` soft-delete.
  {
    key: 'tracing-terminalmap',
    label: 'Tracing Terminal Mapping',
    singular: 'Terminal Mapping',
    group: 'cms',
    route: '/data-management/tracing-terminal-mapping',
    searchPlaceholder: 'Search by Tracing Terminal or Terminal…',
    fields: [
      { key: 'traceterminal', label: 'Tracing Terminal', required: true, fullWidth: true, maxLength: 40, width: 500 },
      { key: 'rail', label: 'Terminal', type: 'select', required: true, optionsEndpoint: '/cms-lookups/rails', align: 'center', width: 100, deletedKey: 'raildeleted' },
    ],
  },
  // SCACs (cms.scacprofiles) — CMS → Steamship Line. Steamship-line SCAC profiles. Bespoke
  // ScacProfileEditDrawer (readonly Bill To cross-ref + trace datetimes, Provider/Pool
  // selects, and an inline chassis-override sub-grid). REVERSED soft-delete on `active` →
  // the grid shows an "Active" column. scac UNIQUE (dup-guard + reactivate). Name/SCAC
  // required; Provider required when Pool != GWI. (Holidays not ported.)
  {
    key: 'scacprofiles',
    label: 'SCACs',
    singular: 'SCAC',
    group: 'cms',
    subgroup: 'Steamship Line',
    route: '/data-management/scacs',
    searchPlaceholder: 'Search SCACs…',
    softDelete: 'active',
    softDeleteInvert: true,
    fields: [
      { key: 'scac', label: 'SCAC', width: 60, editable: false },
      { key: 'name', label: 'Name', width: 190, editable: false },
      { key: 'cccsubscriber', label: 'CCC', type: 'yn', align: 'center', width: 50, editable: false },
      { key: 'preferredchassisprovider', label: 'Provider', align: 'center', width: 90, editable: false },
      { key: 'chassispool', label: 'Pool', align: 'center', width: 90, editable: false },
      { key: 'dotrace', label: 'Trace?', type: 'yn', align: 'center', width: 50, editable: false },
      { key: 'isdown', label: 'Down?', type: 'yn', align: 'center', width: 50, editable: false },
      { key: 'lastattempteddatetime', label: 'Last Attempted', width: 110, editable: false },
      { key: 'lastsuccessfuldatetime', label: 'Last Successful', width: 110, editable: false },
    ],
  },
  // SCAC Bill Tos (cms.scacbillto) — CMS → Steamship Line. Maps a SCAC to its billing
  // accounts. Generic drawer: SCAC is the shared ssline select, Bill To a `type: 'lookup'`
  // async picker (billtos is ~8,400 active — too many to preload).
  // Standard `deleted` soft-delete. SCAC + Bill To required; (scac, billto) is UNIQUE
  // (dup-guard + reactivate). Grid shows the raw billto recno + the joined Bill To name.
  {
    key: 'scacbillto',
    label: 'SCAC Bill Tos',
    singular: 'SCAC Bill To',
    group: 'cms',
    subgroup: 'Steamship Line',
    route: '/data-management/scac-bill-tos',
    searchPlaceholder: 'Search SCAC bill tos…',
    fields: [
      { key: 'scac', label: 'SCAC', type: 'select', optionsEndpoint: '/cms-lookups/scacs',
        required: true, width: 80, fullWidth: true, deletedKey: 'scacdeleted' },
      { key: 'billto', label: 'Bill To', type: 'lookup', lookupTarget: 'billto',
        required: true, fkZeroBlank: true, refNameKey: 'billtoname', deletedKey: 'billtodeleted',
        width: 80, fullWidth: true },
      { key: 'billtoname', label: 'Bill To Name', titleCase: true, width: 220, deletedKey: 'billtodeleted', editable: false },
    ],
  },
  // SCAC Info (cms.scacinfo) — CMS → Steamship Line. Per (SCAC, region) contact + rail-billing
  // reference info. Bespoke ScacInfoEditDrawer (locked-top identity + rpcylocations subtable;
  // scrollable contact groups; locked-bottom notes). SCAC/Location create-only; only SCAC
  // required; (scac, location) UNIQUE (dup-guard + reactivate). Grid "SCAC" shows the joined
  // "SCAC - Name" (computed scacdisplay). importrailbilling / exportrailbilling / chassisrelease
  // are Y/N.
  {
    key: 'scacinfo',
    label: 'SCAC Info',
    singular: 'SCAC Info',
    group: 'cms',
    subgroup: 'Steamship Line',
    route: '/data-management/scac-info',
    searchPlaceholder: 'Search SCAC info…',
    fields: [
      { key: 'scacdisplay', label: 'SCAC', width: 180, editable: false },
      { key: 'location', label: 'Location', width: 120, editable: false },
      { key: 'importrailbilling', label: "Import Rail Billing Req'd", columnLabel: 'Imp Rail Bill', type: 'yn', align: 'center', width: 80, editable: false },
      { key: 'exportrailbilling', label: "Export Rail Billing Req'd", columnLabel: 'Exp Rail Bill', type: 'yn', align: 'center', width: 80, editable: false },
      { key: 'chassisrelease', label: "Chassis Release Req'd", columnLabel: 'Chassis Rel', type: 'yn', align: 'center', width: 80, editable: false },
      { key: 'chassisused', label: 'Chassis Used', width: 120, editable: false },
      { key: 'website', label: 'Website', width: 210, editable: false },
    ],
  },
  // SCAC Locations (cms.scaclocationmap) — CMS → Steamship Line. Per (SCAC, region) rail-port /
  // CY location mapping. Generic drawer: SCAC + Region selects, two `type: 'lookup'` location
  // pickers, Other. Also opened FROM the SCAC Info drawer as its rpcylocations subtable, where
  // ScacLocationMapEditDrawer (now a thin wrapper) pins SCAC + Location via presetValues.
  // Only SCAC required; no unique key. Grid shows the joined rail-port / CY location names.
  {
    key: 'scaclocationmap',
    label: 'SCAC Locations',
    singular: 'SCAC Location',
    group: 'cms',
    subgroup: 'Steamship Line',
    route: '/data-management/scac-locations',
    searchPlaceholder: 'Search SCAC locations…',
    fields: [
      { key: 'scac', label: 'SCAC', type: 'select', optionsEndpoint: '/cms-lookups/scacs',
        required: true, width: 100, fullWidth: true, deletedKey: 'scacdeleted' },
      { key: 'location', label: 'Location', type: 'select', optionsEndpoint: '/cms-lookups/regions',
        width: 120, fullWidth: true },
      { key: 'railportlocid', label: 'Rail Port Location', type: 'lookup', lookupTarget: 'location',
        fkZeroBlank: true, refNameKey: 'rp_lname', deletedKey: 'rpdeleted', inTable: false, fullWidth: true },
      { key: 'rp_lname', label: 'Rail Port', titleCase: true, width: 180, deletedKey: 'rpdeleted', editable: false },
      { key: 'cylocid', label: 'CY Location', type: 'lookup', lookupTarget: 'location',
        fkZeroBlank: true, refNameKey: 'cy_lname', deletedKey: 'cydeleted', inTable: false, fullWidth: true },
      { key: 'cy_lname', label: 'CY', titleCase: true, width: 180, deletedKey: 'cydeleted', editable: false },
      { key: 'other', label: 'Other', maxLength: 100, width: 200, fullWidth: true },
    ],
  },
  // SCAC Empty Locations (cms.scacemptylocations) — CMS → Steamship Line. Per (SCAC, terminal,
  // equipment) empty pickup/return locations, effective-dated. APPEND-ONLY batch model: rows
  // group by dateindex = TO_DAYS(effectivedatetime); a row-click opens the whole effective-date
  // batch in the bespoke ScacEmptyLocationsEditDrawer (existing lines readonly + Deleted toggle;
  // add new lines for the batch's date). Grid shows joined pickup/return names + effective date.
  {
    key: 'scacempty',
    label: 'SCAC Empty Locations',
    singular: 'SCAC Empty Location',
    group: 'cms',
    subgroup: 'Steamship Line',
    route: '/data-management/scac-empty-locations',
    searchPlaceholder: 'Search SCAC empty locations…',
    fields: [
      { key: 'scac', label: 'SCAC', width: 80, deletedKey: 'scacdeleted', editable: false },
      { key: 'terminal', label: 'Terminal', width: 90, editable: false },
      { key: 'chassisprovider', label: 'Chassis Provider', width: 110, editable: false },
      { key: 'size', label: 'Size', align: 'center', width: 70, editable: false },
      { key: 'equiptype', label: 'Type', width: 90, editable: false },
      { key: 'pickup_lname', label: 'Empty Pickup', titleCase: true, width: 160, deletedKey: 'pickupdeleted', editable: false },
      { key: 'terminate_lname', label: 'Empty Return', titleCase: true, width: 160, deletedKey: 'terminatedeleted', editable: false },
      { key: 'effectivedatetime', label: 'Effective', width: 120, editable: false },
    ],
  },
  // Steamship Line Per Diem (cms.sslperdiem) — CMS → Steamship Line. Effective-dated per-diem
  // rate schedules per (SCAC, size, equiptype). Bespoke ScacPerDiemEditDrawer (SCAC/Size/Type
  // selects, effective date, import/export free-days + Business/Calendar day-types, a Tier
  // 1/2/3 amount grid, reuse rules, + a readonly per-diem info block). SCAC required; Tier 3
  // Start > Tier 2 Start when both set; (scac,size,equiptype,effectivedate) UNIQUE (dup-guard
  // + reactivate). `active` = computed "latest effective schedule" flag; `deleted` is the
  // soft-delete. Day-free + tier values show blank when 0.
  {
    key: 'sslperdiem',
    label: 'Steamship Line Per Diem',
    singular: 'Per Diem Schedule',
    group: 'cms',
    subgroup: 'Steamship Line',
    route: '/data-management/ssl-per-diem',
    searchPlaceholder: 'Search per diem schedules…',
    // Column deep-link filters (the Addl Cost "View SSL Per Diem Info" link
    // pre-filters to the order's SCAC / size / equip type).
    filterParams: ['scac', 'size', 'equiptype'],
    fields: [
      { key: 'scac', label: 'SCAC', width: 60, deletedKey: 'scacdeleted', editable: false },
      { key: 'size', label: 'Size', align: 'center', width: 50, editable: false },
      { key: 'equiptype', label: 'Equip Type', width: 100, editable: false },
      { key: 'active', label: 'Active', type: 'yn', align: 'center', width: 50, editable: false },
      { key: 'effectivedate', label: 'Effective Date', align: 'center', width: 100, editable: false },
      { key: 'importdaystype', label: 'Import Days Type', align: 'center', width: 100, editable: false },
      { key: 'importdays', label: 'Import Days Free', align: 'center', width: 90, editable: false },
      { key: 'importamount', label: 'Import Amount/Day', align: 'right', width: 100, editable: false },
      { key: 'importtier2days', label: 'Import Tier 2 Days', align: 'center', width: 90, editable: false },
      { key: 'importtier2amount', label: 'Import Tier 2 Amount', align: 'right', width: 100, editable: false },
      { key: 'exportdaystype', label: 'Export Days Type', align: 'center', width: 100, editable: false },
      { key: 'exportdays', label: 'Export Days Free', align: 'center', width: 90, editable: false },
      { key: 'exportamount', label: 'Export Amount/Day', align: 'right', width: 100, editable: false },
      { key: 'exporttier2days', label: 'Export Tier 2 Days', align: 'center', width: 90, editable: false },
      { key: 'exporttier2amount', label: 'Export Tier 2 Amount', align: 'right', width: 100, editable: false },
      { key: 'reusetype', label: 'Reuse Type', align: 'center', width: 120, editable: false },
      { key: 'reusedays', label: 'Reuse Days', align: 'center', width: 80, editable: false },
      { key: 'reusedaystype', label: 'Reuse Days Type', align: 'center', width: 100, editable: false },
    ],
  },
  // Remaining standalone CMS pages (custom — converted onto the engine later).
  { key: 'dispatch-removal-config', label: 'Dispatch Removal Reasons', group: 'cms', route: '/data-management/dispatch-removal-config', custom: true },
  {
    key: 'dispatcher-groups',
    label: 'Dispatcher Groups',
    singular: 'Dispatcher Group',
    group: 'cms',
    route: '/data-management/dispatcher-groups',
    searchPlaceholder: 'Search groups…',
    // Membership is stored as a ~-joined string in dispatcherlist; the engine maps
    // the multiselect array <-> that column. Options = active GWI dispatchers.
    // Group Name + at least one dispatcher required (both full-width in the drawer).
    fields: [
      { key: 'groupname', label: 'Group Name', required: true, width: 240, fullWidth: true },
      { key: 'dispatchers', label: 'Dispatchers', type: 'multiselect', required: true, width: 360, optionsEndpoint: '/dispatcher-groups/dispatchers', placeholder: 'Select dispatchers…', fullWidth: true },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  { key: 'dispatchers', label: 'Dispatchers', group: 'cms', route: '/data-management/dispatchers', custom: true },
  { key: 'equipment-list', label: 'Equipment List', group: 'cms', route: '/data-management/equipment-list', custom: true },
  // Equipment Locations (cms.equiplocation) — the location list the Equipment List uses.
  // Standard editable/creatable, generic drawer; one required open-text Location field.
  {
    key: 'equiplocation',
    label: 'Equipment Locations',
    singular: 'Equipment Location',
    group: 'cms',
    route: '/data-management/equipment-locations',
    searchPlaceholder: 'Search equipment locations…',
    fields: [
      { key: 'location', label: 'Location', required: true, maxLength: 80, align: 'right', width: 200 },
      { key: 'recno', label: 'Record', type: 'number', width: 90, align: 'right', editable: false },
    ],
  },
  // Equipment Types (cms.equipmenttype) — the equipment-type list the Equipment List /
  // quoting use. Standard editable/creatable, generic drawer; required open-text Type +
  // fixed Quote Mapping dropdown (blank allowed). Legacy allowed duplicate types → no
  // dup-guard.
  {
    key: 'equipmenttype',
    label: 'Equipment Types',
    singular: 'Equipment Type',
    group: 'cms',
    route: '/data-management/equipment-types',
    searchPlaceholder: 'Search equipment types…',
    fields: [
      { key: 'type', label: 'Type', required: true, maxLength: 50, align: 'left', width: 150 },
      { key: 'quotemapping', label: 'Quote Mapping', type: 'select', clearable: true, width: 120, align: 'left', options: [
        'VAN', 'CONTAINER', 'FLATBED', 'RAILCAR', 'HOPPER RAILCAR', 'PNEUMATIC TRUCK', 'DOUBLE DROP', 'SINGLE DROP',
      ] },
      { key: 'recno', label: 'Record', type: 'number', width: 90, align: 'right', editable: false },
    ],
  },
  {
    key: 'invoice-goals',
    label: 'Invoice Goals',
    singular: 'Invoice Goal',
    group: 'billing', // decluttered out of CMS → Billing & Additional Costs
    route: '/data-management/invoice-goals',
    searchPlaceholder: 'Search invoice goals…',
    // Daily/weekly/monthly targets the Invoicing Statistics panel compares against.
    // goaltype is a fixed set + create-only: one active row per type (DB-unique),
    // changed by delete + re-add rather than in place.
    fields: [
      { key: 'goaltype', label: 'Goal Type', type: 'select', required: true, createOnly: true, clearable: false, width: 200, options: [
        'REVENUE', 'ORDERS', 'SENT TO ACCT REVENUE', 'SENT TO ACCT ORDERS', 'AVG DAYS TO BILL', 'AVG DAYS TO ACCT',
      ] },
      { key: 'dailygoal', label: 'Daily Goal', type: 'number', width: 120, align: 'right' },
      { key: 'weeklygoal', label: 'Weekly Goal', type: 'number', width: 120, align: 'right' },
      { key: 'monthlygoal', label: 'Monthly Goal', type: 'number', width: 120, align: 'right' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  { key: 'lane-details', label: 'Lane Details', group: 'cms', route: '/data-management/lane-details', custom: true },
  { key: 'locations', label: 'Locations', group: 'cms', route: '/data-management/locations', custom: true },
  {
    key: 'trouble-ticket-categories',
    label: 'Trouble Ticket Categories',
    singular: 'Trouble Ticket Category',
    group: 'cms',
    route: '/data-management/trouble-ticket-categories',
    searchPlaceholder: 'Search categories…',
    // `deleted` is tinyint(1); the engine maps the Y/N drawer toggle to 0/1 via
    // soft_delete_active/deleted. category is DB-unique → reactivate-on-dup.
    fields: [
      { key: 'category', label: 'Category Name', required: true, width: 280 },
      { key: 'driverticket', label: 'Driver Ticket', type: 'select', required: true, clearable: false, width: 140, defaultValue: 'BOTH', options: [
        { value: 'BOTH', label: 'Both' },
        { value: 'Y', label: 'Yes' },
        { value: 'N', label: 'No' },
      ] },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // fastcms_db lookup tables (no legacy equivalent).
  { key: 'additional-cost-item-types', label: 'Additional Cost Item Types', group: 'billing', route: '/data-management/additional-cost-item-types', custom: true },
  { key: 'driver-pay-addition-types', label: 'Driver Pay Addition Types', group: 'driver', route: '/data-management/driver-pay-addition-types', custom: true },
  // Driver Pay - Open Legs (cms.driverpay_lineitem) — a READ-ONLY view (V1 "Driver
  // Payroll - Open Legs"): open pay lines whose driver no longer matches the leg,
  // or PAY/LEG lines on a deleted/expired rate. NOT maintenance — the only action
  // is the bespoke drawer's "Clear Leg" (blanks driver/pay/approval/rate so the
  // line re-rates, via /driverpay/open-legs/{recno}/clear-leg). createLocked hides
  // Add; noSoftDelete hides the Deleted column/toggle (rows are always non-deleted);
  // the DriverPayOpenLegsDrawer is read-only + Clear Leg. Rate Status is a red flag.
  // Pro # is a proLink (opens the pro drawer; turns green once viewed) so a user
  // working the list can see which pros they've already checked.
  {
    key: 'driverpay-open-legs',
    label: 'Driver Pay - Open Legs',
    singular: 'Open Leg',
    group: 'driverpay',
    route: '/data-management/driver-pay-open-legs',
    searchPlaceholder: 'Search open legs…',
    createLocked: true,
    noSoftDelete: true,
    fields: [
      { key: 'pronumber', label: 'Pro #', proLink: true, width: 120, editable: false },
      { key: 'legnum', label: 'Leg #', width: 60, align: 'center', editable: false },
      { key: 'driver', label: 'Driver', width: 70, align: 'center', editable: false },
      { key: 'eventdate', label: 'Event Date', width: 100, editable: false },
      { key: 'origname', label: 'Origin', titleCase: true, width: 160, deletedKey: 'origdeleted', editable: false },
      { key: 'destname', label: 'Destination', titleCase: true, width: 160, deletedKey: 'destdeleted', editable: false },
      { key: 'paystatus', label: 'Pay Status', width: 90, editable: false },
      { key: 'approvedby', label: 'Approved By', width: 110, editable: false },
      { key: 'approveddatetime', label: 'Approved Date', width: 150, editable: false },
      { key: 'payrolldate', label: 'Payroll Date', width: 100, editable: false },
      { key: 'raterecno', label: 'Rate Recno', width: 90, align: 'center', editable: false },
      { key: 'ratestatus', label: 'Rate Status', redFlag: true, align: 'center', width: 100, editable: false },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },
  // Employee Driver Cost (cms.employeedrivercost) — Driver Pay. Standard editable/creatable
  // config table (generic drawer). Rate is a positive decimal, editable only on create
  // (createOnly → locked on edit); Rate Type is PER HOUR / PER MILE (default PER HOUR);
  // Effective Date uses the shared date picker (type:'date'). All four fields required.
  {
    key: 'employee-driver-cost',
    label: 'Employee Driver Cost',
    singular: 'Employee Driver Cost',
    group: 'driverpay',
    route: '/data-management/employee-driver-cost',
    searchPlaceholder: 'Search by description or rate type…',
    fields: [
      { key: 'description', label: 'Description', required: true, maxLength: 50, width: 220 },
      { key: 'rate', label: 'Rate', type: 'number', required: true, decimal: true, createOnly: true, width: 100 },
      { key: 'ratetype', label: 'Rate Type', type: 'select', required: true, clearable: false, defaultValue: 'PER HOUR', width: 120, options: ['PER HOUR', 'PER MILE'] },
      { key: 'effectivedate', label: 'Effective Date', type: 'date', required: true, width: 130 },
      { key: 'recno', label: 'Recno', type: 'number', width: 80, align: 'right', editable: false },
    ],
  },

  // ──────────────────────────── QUOTE ────────────────────────────
  // Engine-driven (quote schema). Restyled onto <TablemaintPage> in Phase 4.
  {
    key: 'additionalcharges',
    label: 'Transportation Addl Charges',
    singular: 'Transportation Addl Charge',
    group: 'quote',
    route: '/data-management/accessorial-charges',
    searchPlaceholder: 'Search charges…',
    // Master-detail (custom drawer): rates live in additionalchargerates; the
    // Eff. Date / Minimum Rate / Default Rate columns are the CURRENT rate
    // (engine computed columns from the most recent effective-as-of-today rate).
    fields: [
      { key: 'description', label: 'Description', width: 200, titleCase: true, editable: false },
      { key: 'pdfmessage', label: 'PDF Message', width: 150, editable: false },
      { key: 'region', label: 'Region', width: 100, titleCase: true, editable: false },
      { key: 'ratetype', label: 'Rate Type', width: 100, titleCase: true, editable: false },
      { key: 'transload', label: 'Dray', type: 'yn', width: 70, align: 'center', editable: false },
      { key: 'delivery', label: 'Delivery', type: 'yn', width: 80, align: 'center', editable: false },
      { key: 'direct', label: 'Direct', type: 'yn', width: 70, align: 'center', editable: false },
      { key: 'tariff', label: 'Tariff', type: 'yn', width: 70, align: 'center', editable: false },
      { key: 'effectivedate', label: 'Eff. Date', type: 'date', width: 90, editable: false },
      { key: 'minimumrate', label: 'Minimum Rate', type: 'number', width: 100, align: 'right', editable: false },
      { key: 'defaultrate', label: 'Default Rate', type: 'number', width: 100, align: 'right', editable: false },
      // Coverage view of the Quote Charge Mapping (computed; blank = the charge bills
      // under its own description). Edited on the Quote Charge Mapping page.
      { key: 'invoicelitype', label: 'Invoice Line Item', editable: false, width: 160 },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'handlingitems',
    label: 'Handling Addl Charges',
    singular: 'Handling Addl Charge',
    group: 'quote',
    route: '/data-management/handling-items',
    searchPlaceholder: 'Search charges…',
    fields: [
      { key: 'description', label: 'Description', width: 200, required: true, titleCase: true },
      { key: 'pdfmessage', label: 'PDF Message', width: 160 },
      { key: 'ratetype', label: 'Rate Type', type: 'select', width: 110, titleCase: true, options: [
        { value: '', label: '— None —' },
        { value: 'EXTRA', label: 'Extra' },
        { value: 'IN RATE', label: 'In Rate' },
        { value: 'ON INVOICE', label: 'On Invoice' },
      ] },
      { key: 'minimumrate', label: 'Minimum Rate', type: 'number', width: 100, align: 'right' },
      { key: 'defaultrate', label: 'Default Rate', type: 'number', width: 100, align: 'right' },
      // Unit Type options come from cms.unitofmeasure (+ hardcoded set) at runtime.
      { key: 'unittype', label: 'Unit Type', type: 'select', width: 120, optionsEndpoint: '/handling-items/unit-types' },
      { key: 'usewhseinstructions', label: 'Whse Instructions', type: 'select', width: 130, clearable: false, defaultValue: 'QUOTE', options: [
        { value: 'CMS', label: 'CMS' },
        { value: 'QUOTE', label: 'Quote' },
        { value: 'BOTH', label: 'Both' },
      ] },
      { key: 'tariff', label: 'Tariff', type: 'yn', width: 70, align: 'center' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'draystandards',
    label: 'Dray Standards',
    singular: 'Dray Standard',
    group: 'quote',
    route: '/data-management/dray-standards',
    fields: [
      { key: 'warehouse', label: 'Warehouse', type: 'select', required: true, width: 180, options: [
        '', 'BAYPORT', 'BARBOURS CUT', 'BARBOURS CUT/BAYPORT', 'BAYPORT/CERES OOG', 'CERES', 'SENS', 'OTHER',
      ] },
      // Rate Zone options come from cms.ratezones (value=ratezone, label=displayname).
      { key: 'ratezone', label: 'Rate Zone', type: 'select', required: true, width: 180, optionsEndpoint: '/quote-maint/rate-zones' },
      { key: 'minimumrate', label: 'Minimum Rate', type: 'number', width: 110, align: 'right' },
      { key: 'standardrate', label: 'Standard Rate', type: 'number', width: 110, align: 'right' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'deliverystandards',
    label: 'Delivery Standards',
    singular: 'Delivery Standard',
    group: 'quote',
    route: '/data-management/delivery-standards',
    fields: [
      // Rate Zone options come from cms.ratezones (value=ratezone, label=displayname).
      { key: 'ratezone', label: 'Rate Zone', type: 'select', required: true, width: 160, optionsEndpoint: '/quote-maint/rate-zones' },
      { key: 'city', label: 'City', type: 'select', required: true, clearable: false, defaultValue: 'HOUSTON', width: 130, options: CITY_OPTS },
      { key: 'subsection', label: 'Subsection', width: 110 },
      { key: 'state', label: 'State', width: 70 },
      { key: 'zip', label: 'Zip', required: true, width: 80 },
      { key: 'minimumrate', label: 'Minimum Rate', type: 'number', width: 110, align: 'right' },
      { key: 'standardrate', label: 'Standard Rate', type: 'number', width: 110, align: 'right' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'transloadstandards',
    label: 'Handling Standards',
    singular: 'Handling Standard',
    group: 'quote',
    route: '/data-management/transload-standards',
    fields: [
      { key: 'whsetype', label: 'Warehouse Type', type: 'select', required: true, width: 150, options: HS_WHSETYPE_OPTS },
      { key: 'packagetype', label: 'Package Type', type: 'select', required: true, width: 220, options: HS_PACKAGETYPE_OPTS },
      { key: 'movetype', label: 'Move Type', type: 'select', required: true, width: 100, options: HS_MOVETYPE_OPTS },
      { key: 'size20standardrate', label: 'Size 20 Standard Rate', type: 'number', width: 110, align: 'right' },
      { key: 'size20minimumrate', label: 'Size 20 Minimum Rate', type: 'number', width: 110, align: 'right' },
      { key: 'size40standardrate', label: 'Size 40 Standard Rate', type: 'number', width: 110, align: 'right' },
      { key: 'size40minimumrate', label: 'Size 40 Minimum Rate', type: 'number', width: 110, align: 'right' },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'ratezonemileage',
    label: 'Rate Zone Mileage',
    singular: 'Rate Zone Mileage',
    group: 'quote',
    route: '/data-management/rate-zone-mileage',
    // Edited grouped-by-zone in RatezoneMileageEditDrawer; these are list columns.
    fields: [
      { key: 'ratezone', label: 'Rate Zone', width: 180, editable: false },
      { key: 'miles', label: 'Miles', type: 'number', width: 80, align: 'right', editable: false },
      { key: 'ratetype', label: 'Rate Type', width: 100, editable: false },
      { key: 'mileagerate', label: 'Rate', type: 'number', width: 100, align: 'right', editable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'salesreps',
    label: 'Sales Reps',
    singular: 'Sales Rep',
    group: 'quote',
    route: '/data-management/sales-reps',
    fields: [
      // Username: active gwi.users ("username - Full Name"); Region: cms.ratezones.
      { key: 'username', label: 'Username', type: 'select', required: true, width: 160, optionsEndpoint: '/quote-maint/users' },
      { key: 'region', label: 'Region', type: 'select', width: 130, optionsEndpoint: '/quote-maint/regions' },
      { key: 'salestype', label: 'Sales Type', type: 'select', width: 110, clearable: false, defaultValue: 'INSIDE', options: ['INSIDE', 'OUTSIDE'] },
      { key: 'salesforceid', label: 'Salesforce ID', width: 130 },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  {
    key: 'quotepdfmessage',
    label: 'Quote PDF Messages',
    singular: 'Quote PDF Message',
    group: 'quote',
    route: '/data-management/quote-pdf-messages',
    noSoftDelete: true,    // no `deleted` column
    createLocked: true,    // the message types are fixed — users can't add new ones
    fields: [
      // messagetype is fixed (create-only + no create) → shown read-only; only the
      // message is editable, required, and rendered as a large text area.
      { key: 'messagetype', label: 'Message Type', width: 220, createOnly: true },
      { key: 'message', label: 'Message', type: 'textarea', required: true, width: 400 },
    ],
  },
  {
    key: 'salesforce',
    label: 'Salesforce Accounts',
    group: 'quote',
    route: '/data-management/salesforce-accounts',
    readonly: true,
    idKey: 'Id',
    softDelete: 'IsDeleted', // SF-native flag (0/1) — powers the show-deleted toggle
    description: 'Read-only Salesforce account mirror',
    fields: [
      { key: 'Name', label: 'Name', width: 150, editable: false },
      { key: 'BillingStreet', label: 'Address', width: 150, editable: false },
      { key: 'BillingCity', label: 'City', width: 100, editable: false },
      { key: 'BillingState', label: 'State', width: 60, editable: false },
      { key: 'BillingPostalCode', label: 'Zip', width: 70, editable: false },
      { key: 'BillingCountry', label: 'Country', width: 70, editable: false },
      { key: 'Phone', label: 'Phone', width: 110, editable: false },
      { key: 'Fax', label: 'Fax', width: 110, editable: false },
      { key: 'Id', label: 'Id', width: 150, editable: false },
      { key: 'IsDeleted', label: 'IsDeleted', type: 'yn', width: 80, align: 'center', editable: false },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },

  // ──────────────────────────── SAFETY ───────────────────────────
  {
    key: 'repair-categories',
    label: 'Repair Categories',
    singular: 'Repair Category',
    group: 'safety',
    route: '/data-management/repair-categories',
    searchPlaceholder: 'Search categories…',
    // Equipment types are stored as child rows in repaircategory_repairitem; the
    // engine maps the multiselect array <-> those rows.
    fields: [
      { key: 'name', label: 'Category Name', required: true, width: 220 },
      { key: 'repairtype', label: 'Repair Type', type: 'select', required: true, clearable: false, width: 140, defaultValue: 'Mechanical', options: ['Mechanical', 'Cosmetic', 'Preventative'] },
      { key: 'istire', label: 'Tire Category', type: 'yn', width: 100, align: 'center' },
      { key: 'repairitems', label: 'Equipment Types', type: 'multiselect', width: 280, options: [...RepairItemTypes] },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  { key: 'repair-issues', label: 'Repair Issues', group: 'safety', route: '/data-management/repair-issues', custom: true },
  {
    key: 'repair-vendors',
    label: 'Repair Vendors',
    singular: 'Repair Vendor',
    group: 'safety',
    route: '/data-management/repair-vendors',
    searchPlaceholder: 'Search vendors…',
    // Flat soft-delete table. The legacy /repair-vendors endpoint stays mounted —
    // the repair-order drawer creates vendors inline through it.
    fields: [
      { key: 'name', label: 'Vendor Name', required: true, titleCase: true, width: 200 },
      { key: 'phone', label: 'Phone', width: 130 },
      { key: 'vendoremail', label: 'Email', width: 200 },
      { key: 'address1', label: 'Address 1', titleCase: true, width: 180 },
      { key: 'address2', label: 'Address 2', titleCase: true, inTable: false },
      { key: 'city', label: 'City', titleCase: true, width: 120 },
      { key: 'state', label: 'State', maxLength: 2, width: 60 },
      { key: 'zip', label: 'Zip', width: 80 },
      { key: 'country', label: 'Country', titleCase: true, width: 90 },
      { key: 'recno', label: 'Record', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },

  // ──────────────────────────── ADMIN ────────────────────────────
  // V1 dap_syncintervals (api schema). Compact table headers differ from the
  // descriptive drawer labels via columnLabel. syncseconds is an integer.
  {
    key: 'dap-sync-intervals',
    label: 'API Driver Access Sync Intervals',
    singular: 'API Driver Access Sync Interval',
    group: 'admin',
    route: '/data-management/api-sync-intervals',
    searchPlaceholder: 'Search APIs…',
    fields: [
      { key: 'apiname', label: 'API Name', columnLabel: 'API', required: true, maxLength: 50, width: 120 },
      { key: 'apidescription', label: 'API Description', columnLabel: 'Description', maxLength: 50, width: 200, fullWidth: true },
      { key: 'syncseconds', label: 'Sync Interval (seconds)', columnLabel: 'Sync Interval', type: 'number', required: true, maxLength: 5, width: 100 },
    ],
  },
  // V1 available_dbs (db_setup schema). No `deleted` column → noSoftDelete (no
  // Deleted column/show-deleted toggle/drawer checkbox, no delete affordance);
  // Add is still available. available/istest are Y/N checkboxes (render as
  // toggles in the drawer's Options fieldset); recno is shown read-only.
  {
    key: 'available-dbs',
    label: 'Available Database Setup',
    singular: 'Database',
    group: 'admin',
    route: '/data-management/available-dbs',
    searchPlaceholder: 'Search databases…',
    noSoftDelete: true,
    fields: [
      { key: 'name', label: 'Name', required: true, maxLength: 30, width: 150 },
      { key: 'ipaddress', label: 'IP Address', required: true, maxLength: 20, width: 100 },
      { key: 'available', label: 'Available?', columnLabel: 'Available', type: 'yn', width: 60 },
      { key: 'istest', label: 'Test DB?', columnLabel: 'Test DB', type: 'yn', width: 60 },
      { key: 'recno', label: 'Record', type: 'number', width: 60, editable: false },
    ],
  },
  // Order Field Display Rules (fastcms_db.order_field_display_rules, migrations 328–330) —
  // the config-driven DISPLAY layer for the /pro order form: per-movetype (and per-invoiced)
  // rules that hide or lock order FIELDS and TABS. Orthogonal to the WRITE-permission layer
  // (order-lc-fields / leg-editing / …), which stays in code; effective editability =
  // user-may-write AND shown AND not-readonly. Lives in the app's own DB (backend app_db,
  // id PK) — generic field-spec drawer, standard `deleted` soft-delete + edit history. The
  // UNIQUE key (target_type,target,scope,behavior,system) is dup-guarded + reactivate_on_dup.
  {
    key: 'order-field-display-rules',
    label: 'Order Field Display Rules',
    singular: 'Order Field Display Rule',
    group: 'admin',
    route: '/data-management/order-field-display-rules',
    searchPlaceholder: 'Search by field/tab, scope, or note…',
    idKey: 'id',
    groupBy: ['scope'],
    fields: [
      {
        key: 'target_type', label: 'Target Type', type: 'select', required: true,
        clearable: false, defaultValue: 'field', width: 90, align: 'center',
        options: [
          { value: 'field', label: 'Field' },
          { value: 'tab', label: 'Tab' },
        ],
      },
      {
        key: 'target', label: 'Target', required: true, maxLength: 64, width: 180,
        placeholder: 'Legacy DB column name (field) or tab id (tab)',
      },
      {
        key: 'scope', label: 'Scope', type: 'select', required: true, clearable: false, width: 130,
        options: ['DOMESTIC', 'EXPORT', 'IMPORT', 'INBOUND', 'OUTBOUND', 'RAIL TO PORT', 'REPO', 'INVOICED', 'ALL'],
      },
      {
        key: 'behavior', label: 'Behavior', type: 'select', required: true,
        clearable: false, defaultValue: 'hidden', width: 100, align: 'center',
        options: [
          { value: 'hidden', label: 'Hidden' },
          { value: 'readonly', label: 'Read-only' },
        ],
      },
      // Admin Override (#33501): may a pro-admin-override holder unlock this rule on one
      // pro, for one session? Defaults No so a rule added here is not silently
      // bypassable. Only meaningful on a Read-only rule — the server refuses Yes on a
      // Hidden one, since hiding says "meaningless for this movetype", not "locked".
      {
        key: 'overridable', label: 'Overridable', type: 'select', required: true,
        clearable: false, defaultValue: 'N', width: 100, align: 'center',
        options: [
          { value: 'N', label: 'No' },
          { value: 'Y', label: 'Yes' },
        ],
      },
      {
        key: 'system', label: 'System', type: 'select', required: true,
        clearable: false, defaultValue: 'cms', width: 90, align: 'center',
        options: [
          { value: 'cms', label: 'CMS' },
          { value: 'template', label: 'Template' },
        ],
      },
      { key: 'note', label: 'Note', maxLength: 255, fullWidth: true, width: 220 },
      { key: 'updated_at', label: 'Updated', editable: false, width: 150 },
      { key: 'id', label: 'ID', type: 'number', width: 60, align: 'right', editable: false },
    ],
  },
  // Printers (cms.printers) — the printer catalogue. Config-only, generic drawer + standard
  // `deleted` soft-delete. Display/Install Name are open text (both required); Type is a
  // LASER/LABEL select (default LASER); Driver Access is Y/N (default N); Driver Access Name
  // is open text, required when Driver Access is on (backend-enforced). Uniqueness on
  // (installname, driveraccessname) → dup-guard + reactivate. The drawer carries a
  // "Print Test Page" action (print-send gated, LASER rows only) so an admin can prove a
  // queue reaches paper without leaving the catalogue.
  {
    key: 'printers',
    label: 'Printers',
    singular: 'Printer',
    group: 'admin',
    route: '/data-management/printers',
    searchPlaceholder: 'Search printers…',
    drawerActions: ['print-test-page'],
    fields: [
      { key: 'displayname', label: 'Display Name', required: true, maxLength: 30, width: 150 },
      { key: 'installname', label: 'Install Name', required: true, maxLength: 30, width: 150 },
      { key: 'type', label: 'Type', type: 'select', clearable: false, defaultValue: 'LASER', options: ['LASER', 'LABEL'], width: 150 },
      { key: 'driveraccess', label: 'Driver Access', type: 'yn', align: 'center', width: 80 },
      { key: 'driveraccessname', label: 'Driver Access Name', requiredWhen: { field: 'driveraccess', equals: 'Y' }, maxLength: 30, width: 150 },
      { key: 'recno', label: 'Recno', type: 'number', width: 70, align: 'right', editable: false },
    ],
  },
  // Report Monitoring Master (cms.reportmaster) — per-report monitoring + notification
  // config (twin of EDI Monitoring Master). Config-only, generic drawer via
  // showReadOnlyContext (System / Name / Created / Last Run render read-only) plus a second
  // "View Report History" link (extraHistoryLink) that opens the history grid filtered to
  // this row's system + name. NO adds (createLocked). Editable: Notification Days (int),
  // Type of Days (Business/Calendar), Comments (textarea), Email (comma-delimited, validated).
  // Nothing required. No unique key.
  {
    key: 'reportmaster',
    label: 'Report Monitoring Master',
    singular: 'Report Monitoring Entry',
    group: 'admin',
    route: '/data-management/report-monitoring-master',
    searchPlaceholder: 'Search by Report Name, System, or User…',
    createLocked: true,
    showReadOnlyContext: true,
    extraHistoryLink: {
      label: 'View Report History',
      href: (row) =>
        `/data-management/report-monitoring-history?system=${encodeURIComponent(String(row.system ?? ''))}&name=${encodeURIComponent(String(row.name ?? ''))}`,
    },
    fields: [
      { key: 'system', label: 'System', width: 55, editable: false },
      { key: 'name', label: 'Name', width: 150, editable: false },
      { key: 'createddatetime', label: 'Created Date/Time', width: 120, editable: false },
      { key: 'lastrunuser', label: 'Last Run By User', width: 100, editable: false },
      { key: 'lastrundatetime', label: 'Last Run Date/Time', width: 120, editable: false },
      { key: 'notificationdays', label: 'Notification Days', type: 'number', width: 90 },
      { key: 'ndaystype', label: 'Type of Days', type: 'select', width: 100, options: [
        { value: 'business', label: 'Business' },
        { value: 'calendar', label: 'Calendar' },
      ] },
      { key: 'comments', label: 'Comments', type: 'textarea', fullWidth: true, width: 150 },
      { key: 'email', label: 'Email', type: 'textarea', email: true, fullWidth: true, width: 140 },
    ],
  },
  // Report Monitoring History (cms.reporthistory) — ~500k report-run audit rows. READONLY +
  // server-searched grid; the "View Report History" link on the Master opens this pre-filtered
  // to a report's system + name (filterParams → backend exact filters). No adds/edits, no
  // soft-delete. Search box filters server-side across System / Name / User / Parameters.
  {
    key: 'reporthistory',
    label: 'Report Monitoring History',
    singular: 'Report History Entry',
    group: 'admin',
    route: '/data-management/report-monitoring-history',
    searchPlaceholder: 'Search by System, Report Name, User, or Parameters…',
    readonly: true,
    serverSearch: true,
    noSoftDelete: true,
    filterParams: ['system', 'name'],
    fields: [
      { key: 'system', label: 'System', width: 120 },
      { key: 'name', label: 'Report Name', width: 140 },
      { key: 'userid', label: 'User ID', width: 150 },
      { key: 'rundatetime', label: 'Run Date/Time', width: 120 },
      { key: 'parameters', label: 'Parameters', width: 420 },
    ],
  },
  // Proxies (aa_tracing.proxies) — the outbound proxy pool the legacy Gnosis container-tracking
  // crawler rotates through. Lives in TRACING_SCHEMA (backend schema="tracing"); history → cms.
  // Config-only, generic drawer. Type is a small dropdown (blank / https / socks); Hostname / IP
  // Address / Port / Login / Password are free text; Is Down + Deleted are Y/N; Last Attempted /
  // Last Successful are read-only crawler timestamps. Nothing required, no unique key.
  {
    key: 'tracing-proxies',
    label: 'Proxies',
    singular: 'Proxy',
    group: 'admin',
    route: '/data-management/tracing-proxies',
    searchPlaceholder: 'Search by Hostname, IP Address, or Type…',
    fields: [
      { key: 'recno', label: 'Recno', type: 'number', width: 45, align: 'right', editable: false },
      { key: 'hostname', label: 'Hostname', width: 170, maxLength: 40 },
      { key: 'type', label: 'Type', type: 'select', width: 60, options: [
        { value: 'https', label: 'https' },
        { value: 'socks', label: 'socks' },
      ] },
      { key: 'ipaddress', label: 'IP Address', width: 100, maxLength: 20 },
      { key: 'port', label: 'Port', width: 70, maxLength: 10 },
      { key: 'login', label: 'Login', width: 90, maxLength: 20 },
      { key: 'password', label: 'Password', width: 90, maxLength: 20 },
      { key: 'lastattempteddatetime', label: 'Last Attempted', width: 120, editable: false },
      { key: 'lastsuccessfuldatetime', label: 'Last Successful', width: 120, editable: false },
      { key: 'isdown', label: 'Is Down', type: 'yn', align: 'center', width: 60 },
    ],
  },
  { key: 'developer-activity', label: 'Developer Activity', group: 'admin', route: '/data-management/developer-activity', custom: true },
  { key: 'field-manager', label: 'Field Manager', group: 'admin', route: '/data-management/field-manager', custom: true },
  { key: 'join-manager', label: 'Join Manager', group: 'admin', route: '/data-management/join-manager', custom: true },
];

/**
 * Engine tables whose backend route is live and safe to surface in the hub /
 * global search. Custom tables are always ready (their routes already exist);
 * engine tables become ready as they're wired:
 *   - Phase 3: 'billtos', 'customers'
 *   - Phase 4: the quote keys
 */
export const WIRED_ENGINE_TABLES = new Set<string>([
  'billtos',
  'customers',
  'fsc-tariff',
  'fsc-customer',
  'fsc-driver',
  'customer-perdiem',
  // Phase 5: CMS + Safety standalone-page conversions
  'contact-types',
  // Wave 2
  'brokerforwarder',
  'carrierprofile',
  'ccc-standards',
  'outboundmode',
  'chassisprovider',
  'contstatusreport',
  'expreport',
  'cmscodes',
  'equiplocation',
  'equipmenttype',
  'dc-map',
  'csrgroups',
  'customcsrgroup',
  'customchassischarges',
  'daltile-templatemap',
  'daltile-carrier-map',
  'thd-templatemap',
  'thd-dc',
  'thd-exception',
  'upload-brokermap',
  'driverpay-open-legs',
  'employee-driver-cost',
  'edi-997',
  'edi-chargecodemap',
  'edi-csrs',
  'edi-document-setup',
  'edi-equipmentmap',
  'edi-invoice-rules',
  'edi-invoice-x12-partners',
  'edi-events',
  'edi-inventory-adj',
  'edi-locations',
  'edi-pickuplocationmap',
  'edimaster',
  'edi-orders',
  'edi-updatefiles',
  'edi-portmap',
  'edi-scacmap',
  'edi-sentinvoices',
  'edi-invoice-setup',
  'kleinschmidt-partners',
  'kleinschmidt-events',
  'melrose-templatemap',
  'melrose-locationmap',
  'melrose-billtomap',
  'melrose-scacmap',
  'melrose-equipmentmap',
  'melrose-orders',
  // Wave 2: Billing & Additional Costs
  'additionalcost-dispute-reasons',
  'additionalcost-hold-types',
  'additionalcost-reasons',
  'glcodes',
  'invoice-litypes',
  'invoice-litype-charge-map',
  'invoice-holdreasons',
  'unbilled-reasons',
  'paperwork-rejection-reasons',
  'hazmatdata',
  'markets',
  'overweight-exceptions',
  'ports',
  'printers',
  'rails',
  'ratezones',
  'reportemail',
  'reportmaster',
  'reporthistory',
  'reschedulereason',
  'scacprofiles',
  'scacbillto',
  'scacinfo',
  'scaclocationmap',
  'scacempty',
  'sslperdiem',
  'tracing-proxies',
  'tracing-terminalmap',
  'upload-equipmentmap',
  'upload-portmap',
  'upload-glcodemap',
  'vendors',
  'dap-sync-intervals',
  'available-dbs',
  'order-field-display-rules',
  'invoice-goals',
  'repair-vendors',
  'trouble-ticket-categories',
  'dispatcher-groups',
  'repair-categories',
  // Phase 4: quote lookup tables
  'additionalcharges',
  'handlingitems',
  'draystandards',
  'deliverystandards',
  'transloadstandards',
  'ratezonemileage',
  'salesreps',
  'quotepdfmessage',
  'salesforce',
]);

/** A table is shown in the hub/search once its route is live. */
export const isTableReady = (t: DataTableDef): boolean =>
  !!t.custom || WIRED_ENGINE_TABLES.has(t.key);

/** Engine-driven tables (rendered by <TablemaintPage>). */
export const engineTables = (): DataTableDef[] => DATA_TABLES.filter((t) => !t.custom);

/** Look up a table by its registry key. */
export const findTable = (key: string): DataTableDef | undefined =>
  DATA_TABLES.find((t) => t.key === key);

/** Look up a table by its route path. */
export const findTableByRoute = (route: string): DataTableDef | undefined =>
  DATA_TABLES.find((t) => t.route === route);

/** Tables grouped by subcategory, in display order — for the hub page. */
export const tablesByGroup = (): { group: TableGroup; label: string; tables: DataTableDef[] }[] =>
  GROUP_ORDER.map((group) => ({
    group,
    label: GROUP_LABELS[group],
    tables: DATA_TABLES.filter((t) => t.group === group),
  })).filter((g) => g.tables.length > 0);
